import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, Dispatch, KeyboardEvent, MouseEvent, ReactNode, RefObject, SetStateAction } from 'react'
import { palettePrimaryMain as PRIMARY, palettePrimaryMutedSurface as PRIMARY_MUTED } from '@/theme/designTokens'
import { env } from '@/config/env'
import type { IQuestionBankEntryDetail, IQuestionBankEntrySummary } from '@brushfire/types'
import { QuestionBankBookmarkButton } from '@/features/question-bank/components/QuestionBankBookmarkButton'
import { ProfileUpdateQuestionButton } from './ProfileUpdateQuestionButton'
import { McCbPreviewOptionRow, type PreviewSelKey } from './mcCbLivePreviewRow'
import type { ProfileLink } from './profile-attributes.api'
import { HideQuestionDialog } from './HideQuestionDialog'
import { QuestionFromTemplateBadge } from './QuestionFromTemplateBadge'
import { VideoGuidelinesPanels, VideoPreviewCard, VideoQuestionSection } from './VideoQuestionSection'
import { VideoPlaybackEmbed } from './VideoPlaybackEmbed'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import {
  RiAddCircleLine,
  RiAddFill,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiDeleteBinLine,
  RiSubtractFill,
  RiUpload2Line,
} from 'react-icons/ri'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Slider from '@mui/material/Slider'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Tooltip from '@mui/material/Tooltip'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { CampaignCalendarPickerIcon } from '@/features/campaigns/components/CampaignCalendarPickerIcon'
import type {
  BuilderQuestion,
  CheckboxConfig,
  MultipleChoiceConfig,
  DateConfig,
  DateInputDisplayFormat,
  DropdownConfig,
  LikertConfig,
  PictureChoiceConfig,
  PictureChoiceImageSize,
  PictureChoiceOptionRow,
  RankingConfig,
  RatingConfig,
  RatingDisplayStyle,
  RatingScaleMax,
  ShortTextConfig,
  SliderConfig,
  YesNoConfig,
} from './types'
import {
  SHORT_TEXT_MAX_LENGTH_PLACEHOLDER,
  SHORT_TEXT_MIN_LENGTH_PLACEHOLDER,
} from './types'
import { DISPLAY_SWITCH_SX } from './displaySettingSwitchSx'
import { pictureChoiceDimensions } from './picture-choice-sizes'
import {
  BuilderSaveValidationContext,
  gateFieldError,
  gateFieldErrorFlag,
  useGatedBuilderFieldError,
  useShowBuilderSaveValidation,
} from './builderSaveValidationContext'
import {
  composeWholeFieldMarkdown,
  countFilledAnswerOptions,
  defaultBuilderTextFormatFlags,
  errorDateRangeOrder,
  errorInvalidPositiveIntegerField,
  errorMinMaxOrder,
  errorMinMaxSelection,
  errorOptionsNeedTwoNonEmpty,
  errorOptionalIsoDateField,
  errorPictureOptionsNeedTwoImages,
  errorRankingOptionsNeedTwoNonEmpty,
  errorLikertScaleLabelRequired,
  errorQuestionHeadingRequired,
  errorQuestionTextRequired,
  errorShortTextLengthOrder,
  isShortTextLikeConfig,
  isTextAnswerConfig,
  LIKERT_LABEL_MAX,
  LIKERT_POINT_COUNT_MAX,
  LIKERT_POINT_COUNT_MIN,
  clampLikertPointCount,
  likertLabelsForPointCount,
  newObjectIdHex,
  optionIdsForLabels,
  withoutIndexAt,
  DATE_HINT_PLACEHOLDER_MAX,
  RANKING_ITEM_TEXT_MAX,
  sanitizePositiveIntegerDigits,
  selectionExceedsAnswerOptionCount,
  SLIDER_END_LABEL_MAX,
  sliderRangeValidationMessage,
  splitWholeFieldMarkdown,
  isTextResponseOn,
  pruneTextResponse,
  textResponseOptionLabel,
  toggleTextResponse,
  type ChoiceTextResponseConfig,
} from '@/features/instruments/domain'
import { AddTextSwitch, TextInputSettingsPanel } from './ChoiceTextResponseControls'
import {
  EMPTY_ROUTING_OPTION_IDS,
  RoutingOptionIdsContext,
  useRoutingOptionIds,
} from './routingOptionIdsContext'
import { ExclusiveRoutingAnswerWarning } from '@/features/instruments/routing/ExclusiveRoutingAnswerWarning'
import { CHECKBOX_SELECT_ALL_CAPTION } from '@/features/instruments/domain/types'
import { BUILDER_QUESTION_TEXT_MAX } from '@/features/instruments/domain/builderFieldMessages'
import {
  alignSliderPreviewValue,
  bumpSliderMaxString,
  bumpSliderMinString,
  bumpSliderStepString,
  dayFromStorageIso,
  pinchMaxDateWithFutureRestriction,
  pinchMinDateWithPastRestriction,
  effectiveSliderMaxValue,
  effectiveSliderMinValue,
  sanitizeSliderMaxFieldInput,
  sanitizeSliderMinFieldInput,
  shuffleDeterministic,
  sliderInclusiveSpan,
  storageIsoFromDay,
} from './questionBuilderPanelHelpers'
import {
  pictureChoiceMaxFileHumanMb,
  uploadBuilderOptionImage,
} from './uploadOptionImage'

const CARD_BORDER = '#e1e7f3'
const SECONDARY = '#64748b'
const PREVIEW_CARD_BG = '#f0f8fd'
const PREVIEW_CARD_BORDER = '#e6f4fb'
const PREVIEW_OTHER_LABEL = 'Other'
const PREVIEW_NONE_LABEL = 'None of the above'

/** Figma 4398:6951 — instrument preview dialog question title (Q{n}. inline prompt). */
export const INSTRUMENT_PREVIEW_QUESTION_TITLE_SX = {
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '30px',
  color: '#0d1b2a',
} as const

/** Figma 4398:6953 — instrument preview dialog question description. */
export const INSTRUMENT_PREVIEW_QUESTION_DESC_SX = {
  fontSize: '14px',
  lineHeight: '21px',
  color: '#6b7a90',
  whiteSpace: 'pre-wrap',
} as const

/** Required asterisk on preview question prompts (matches Signal Provider survey execution). */
export const INSTRUMENT_PREVIEW_REQUIRED_MARK_SX = {
  color: '#ff5959',
} as const

export function InstrumentPreviewRequiredMark() {
  return (
    <Box
      component="span"
      sx={{ ...INSTRUMENT_PREVIEW_REQUIRED_MARK_SX, display: 'inline' }}
      aria-hidden
      data-testid="instrument-preview-required-mark"
    >
      {'\u00a0*'}
    </Box>
  )
}

type QuestionBuilderSurfacePatch = Partial<
  Pick<
    BuilderQuestion,
    | 'questionText'
    | 'description'
    | 'mandatory'
    | 'hidden'
    | 'questionTextFormat'
    | 'descriptionFormat'
  >
> & { config?: BuilderQuestion['config'] }

dayjs.extend(customParseFormat)

const SLIDER_NUM_STEPPER_BTN_SX = {
  p: '1px',
  m: 0,
  minWidth: 28,
  width: 28,
  height: 20,
  borderRadius: 0,
  color: '#6b7a90',
  '&:hover': { bgcolor: 'rgba(13, 27, 42, 0.06)' },
} as const

/** OutlinedInput: pull end stepper toward inner right (MUI default `adornedEnd` is 14px). */
const SLIDER_RANGE_TEXTFIELD_OUTLINED_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    minHeight: 44,
    '&.MuiInputBase-adornedEnd': {
      paddingRight: '8px',
    },
  },
} as const

function preventAdornmentMouseDown(e: MouseEvent) {
  e.preventDefault()
}

/** Figma 4399:9267 / 9272 / 9282 — up/down hug inner-right of outline (kill MUI end gap + default adornment margin). */
function SliderNumericStepperAdornment({
  ariaLabelUp,
  ariaLabelDown,
  onIncrement,
  onDecrement,
  testIdUp,
  testIdDown,
}: Readonly<{
  ariaLabelUp: string
  ariaLabelDown: string
  onIncrement: () => void
  onDecrement: () => void
  testIdUp: string
  testIdDown: string
}>) {
  return (
    <InputAdornment
      position="end"
      sx={{
        m: 0,
        marginLeft: 0,
        marginRight: 0,
        maxHeight: 'none',
        height: 'auto',
        alignSelf: 'stretch',
        display: 'inline-flex',
        alignItems: 'stretch',
      }}
    >
      <Box
        component="span"
        data-testid="slider-numeric-stepper-align"
        sx={{
          display: 'inline-flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'stretch',
          alignSelf: 'stretch',
          height: '100%',
          minHeight: 44,
        }}
      >
        <Stack
          direction="column"
          justifyContent="center"
          component="span"
          data-testid="slider-numeric-stepper"
          sx={{
            alignItems: 'flex-end',
            alignSelf: 'stretch',
            minHeight: 44,
            py: 0.25,
          }}
        >
          <IconButton
            type="button"
            size="small"
            aria-label={ariaLabelUp}
            data-testid={testIdUp}
            onMouseDown={preventAdornmentMouseDown}
            onClick={onIncrement}
            sx={SLIDER_NUM_STEPPER_BTN_SX}
          >
            <RiArrowUpSLine size={18} aria-hidden />
          </IconButton>
          <IconButton
            type="button"
            size="small"
            aria-label={ariaLabelDown}
            data-testid={testIdDown}
            onMouseDown={preventAdornmentMouseDown}
            onClick={onDecrement}
            sx={SLIDER_NUM_STEPPER_BTN_SX}
          >
            <RiArrowDownSLine size={18} aria-hidden />
          </IconButton>
        </Stack>
      </Box>
    </InputAdornment>
  )
}

function datePickerFormatToken(display: DateInputDisplayFormat): string {
  return display === 'MM/dd/yyyy' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
}

/** Min/max for the live preview picker from panel toggles + bounds (ISO storage). */
function previewDateBoundsFromConfig(d: DateConfig): {
  minDate: Dayjs | undefined
  maxDate: Dayjs | undefined
  blockReason: string | null
} {
  const earliestFmt = errorOptionalIsoDateField(d.earliestSelectableDate)
  const latestFmt = errorOptionalIsoDateField(d.latestSelectableDate)
  const rangeErr = errorDateRangeOrder(d.earliestSelectableDate, d.latestSelectableDate)
  if (earliestFmt || latestFmt || rangeErr) {
    return { minDate: undefined, maxDate: undefined, blockReason: earliestFmt ?? latestFmt ?? rangeErr }
  }
  const eTrim = d.earliestSelectableDate.trim()
  const lTrim = d.latestSelectableDate.trim()
  const eDay = eTrim ? dayFromStorageIso(eTrim) : null
  const lDay = lTrim ? dayFromStorageIso(lTrim) : null
  const today = dayjs().startOf('day')

  let minDate = eDay?.isValid() ? eDay.startOf('day') : undefined
  let maxDate = lDay?.isValid() ? lDay.startOf('day') : undefined

  if (d.disablePastDates) {
    minDate = pinchMinDateWithPastRestriction(minDate, today)
  }
  if (d.disableFutureDates) {
    maxDate = pinchMaxDateWithFutureRestriction(maxDate, today)
  }

  if (minDate && maxDate && minDate.isAfter(maxDate)) {
    return {
      minDate: undefined,
      maxDate: undefined,
      blockReason: 'No selectable dates — adjust restrictions or bounds.',
    }
  }
  return { minDate, maxDate, blockReason: null }
}

/** Figma 4399:9078 / 4399:9085 / 4399:9103 — outlined date field (44px); inline calendar icon, no gray well. */
export const INSTRUMENT_DATE_PICKER_FIELD_HEIGHT_PX = 44
/** Figma 4399:9103 — trailing calendar glyph size. */
export const INSTRUMENT_DATE_PICKER_OPEN_ICON_SIZE_PX = 20

const INSTRUMENT_DATE_PICKER_OPEN_BUTTON_SX = {
  p: '4px',
  mr: '4px',
  color: '#4a5565',
  borderRadius: '4px',
  bgcolor: 'transparent',
  '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
  '&.Mui-focusVisible': {
    outline: `2px solid ${PRIMARY}`,
    outlineOffset: 2,
  },
  '&.Mui-disabled': { opacity: 0.55 },
} as const

const INSTRUMENT_DATE_CALENDAR_SLOT_PROPS = {
  desktopPaper: {
    sx: {
      borderRadius: '10px',
      border: '1px solid #e4e9f0',
      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden',
    },
  },
  layout: {
    sx: {
      '& .MuiPickersDay-root': { fontSize: '14px' },
      '& .MuiPickersDay-root.Mui-selected': {
        bgcolor: PRIMARY,
        color: '#fff',
        fontWeight: 600,
        '&:hover, &.Mui-focusVisible': { bgcolor: PRIMARY },
      },
      '& .MuiPickersDay-root:not(.Mui-selected).MuiPickersDay-today': {
        border: `1px solid ${PRIMARY}`,
        fontWeight: 600,
      },
    },
  },
  openPickerIcon: {
    sx: { fontSize: INSTRUMENT_DATE_PICKER_OPEN_ICON_SIZE_PX, color: '#4a5565', display: 'block' },
  },
  openPickerButton: { sx: INSTRUMENT_DATE_PICKER_OPEN_BUTTON_SX },
} as const

/** Plain outlined field with inline calendar control — not the prior #e1e7f3 45×44 well. */
const INSTRUMENT_DATE_PICKER_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#fff',
    minHeight: INSTRUMENT_DATE_PICKER_FIELD_HEIGHT_PX,
    alignItems: 'center',
    pr: '4px',
  },
  '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
    paddingRight: '8px',
  },
  '& .MuiOutlinedInput-input': {
    py: '11px',
    px: '12px',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e4e9f0',
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: '#e4e9f0',
    },
  '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
    marginLeft: 0,
    marginRight: 0,
    maxHeight: 'none',
    height: 'auto',
    alignSelf: 'center',
    bgcolor: 'transparent',
    border: 'none',
    borderRadius: 0,
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': INSTRUMENT_DATE_PICKER_OPEN_BUTTON_SX,
} as const

const INSTRUMENT_DATE_PICKER_SLOTS = {
  openPickerIcon: CampaignCalendarPickerIcon,
} as const

/** Cap live-preview date field width (was 790px; half keeps picker readable without spanning the config card). */
const DATE_LIVE_PREVIEW_FIELD_MAX_WIDTH_PX = 395

/** Figma 4399:9458 — selected date-format chip. */
export const DATE_FORMAT_CHIP_SELECTED_BG = '#008cd0'

/** Figma 4399:9460 — deselected date-format chip border. */
export const DATE_FORMAT_CHIP_BORDER_COLOR = '#e4e9f0'

/** Figma 4399:9458 / 4399:9460 — chip height and horizontal gap (8px between frames). */
export const DATE_FORMAT_CHIP_HEIGHT_PX = 36
export const DATE_FORMAT_CHIP_GAP_PX = 8
/** Figma 4399:9458 — DD/MM/YYYY button frame width. */
export const DATE_FORMAT_CHIP_DD_MM_WIDTH_PX = 124
/** Figma 4399:9460 — MM/DD/YYYY button frame width. */
export const DATE_FORMAT_CHIP_MM_DD_WIDTH_PX = 126

const DATE_FORMAT_CHIP_SX = {
  flex: 'none',
  boxSizing: 'border-box',
  minHeight: DATE_FORMAT_CHIP_HEIGHT_PX,
  height: DATE_FORMAT_CHIP_HEIGHT_PX,
  py: '7.5px',
  px: '15px',
  borderRadius: '10px',
  textTransform: 'none' as const,
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 600,
  letterSpacing: '-0.1504px',
  border: `1px solid ${DATE_FORMAT_CHIP_BORDER_COLOR}`,
  bgcolor: '#fff',
  color: '#0d1b2a',
  '&:hover': { bgcolor: '#f8fafc' },
  '&:not(.Mui-selected)': {
    border: `1px solid ${DATE_FORMAT_CHIP_BORDER_COLOR}`,
    bgcolor: '#fff',
    color: '#0d1b2a',
  },
  '&.Mui-selected': {
    bgcolor: DATE_FORMAT_CHIP_SELECTED_BG,
    color: '#fff',
    borderColor: DATE_FORMAT_CHIP_SELECTED_BG,
    '&:hover': { bgcolor: DATE_FORMAT_CHIP_SELECTED_BG },
  },
  '&.Mui-selected.Mui-disabled': {
    bgcolor: DATE_FORMAT_CHIP_SELECTED_BG,
    color: '#fff',
    opacity: 0.55,
  },
} as const

type YnPreviewChoice = 'yes' | 'no' | null

function parsePositiveIntStrict(raw: string): number | undefined {
  const t = raw.trim()
  if (!t) return undefined
  if (!/^[1-9]\d*$/.test(t)) return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : undefined
}

function filledOptionRows(options: readonly string[]): { index: number; label: string }[] {
  const out: { index: number; label: string }[] = []
  for (let i = 0; i < options.length; i++) {
    const label = options[i]?.trim() ?? ''
    if (label) out.push({ index: i, label })
  }
  return out
}

function optKey(index: number): PreviewSelKey {
  return `opt:${index}`
}

/** Multiple choice uses single-select preview unless min or max implies multi-select. */
function mcPreviewIsSingleSelect(minSelection: string, maxSelection: string): boolean {
  const minV = parsePositiveIntStrict(minSelection)
  const maxV = parsePositiveIntStrict(maxSelection)
  if (minV !== undefined && minV > 1) return false
  if (maxV !== undefined && maxV > 1) return false
  return true
}

/**
 * Routed option ids that cannot be combined with a different path.
 * Checkbox is not routing-eligible (BFP-251), so its preview stays multi-select.
 */
function exclusiveRoutingOptionIdsFor(
  config: BuilderQuestion['config'],
  routingOptionIds: ReadonlySet<string>,
): ReadonlySet<string> {
  if (
    config.type === 'MULTIPLE_CHOICE' &&
    !mcPreviewIsSingleSelect(config.data.minSelection, config.data.maxSelection)
  ) {
    return routingOptionIds
  }
  return EMPTY_ROUTING_OPTION_IDS
}

function withoutIndex<T>(arr: readonly T[], index: number): T[] {
  return arr.filter((_, j) => j !== index)
}

/** Figma 4399:9452 — Display Settings title (margins applied on section wrapper when standalone) */
const DISPLAY_SETTINGS_TITLE_TEXT_SX = {
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.15px',
  color: '#0f172a',
} as const

/** Standalone heading (e.g. checkbox) — full-width title with spacing */
const DISPLAY_SETTINGS_HEADING_SX = {
  ...DISPLAY_SETTINGS_TITLE_TEXT_SX,
  mt: 3,
  mb: 2,
} as const

/** Body label ash #4A5565, 16px gap to switch (Figma gap-[16px]) */
const DISPLAY_FORM_CONTROL_LABEL_SX = {
  ml: 0,
  mr: 0,
  alignItems: 'flex-start',
  gap: 2,
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#4a5565',
    pt: '2px',
  },
} as const

/** Figma 4399:8659 — "Special Options" (Text head_Bold_16) */
const SPECIAL_OPTIONS_HEADING_SX = {
  ...DISPLAY_SETTINGS_TITLE_TEXT_SX,
  mt: 3,
  mb: 1.5,
} as const

/** Figma 4399:9444 / 4399:9446 — body label #0f1729 (darker than display-settings row) */
const SPECIAL_OPTIONS_FORM_CONTROL_LABEL_SX = {
  ...DISPLAY_FORM_CONTROL_LABEL_SX,
  '& .MuiFormControlLabel-label': {
    ...DISPLAY_FORM_CONTROL_LABEL_SX['& .MuiFormControlLabel-label'],
    color: '#0f1729',
  },
} as const

/** Figma 4399:8987 — Yes/No option labels (counter shows n / 50). */
const YN_LABEL_MAX = 50

const YN_QUICK_PRESETS = [
  { yes: 'Yes', no: 'No', buttonLabel: 'Yes / No' },
  { yes: 'Agree', no: 'Disagree', buttonLabel: 'Agree / Disagree' },
  { yes: 'Valid', no: 'Invalid', buttonLabel: 'Valid / Invalid' },
  { yes: 'True', no: 'False', buttonLabel: 'True / False' },
] as const

const ANSWER_OPTION_FIELD_MAX = 100
const PICTURE_CAPTION_MAX = 100

/** Figma 4399:9478 — ranking editor / live-preview rows (drag, rank badge, 38px thumb, caption). */
const RANKING_ROW_SURFACE_SX = {
  bgcolor: '#fff',
  border: '1px solid #e4e9f0',
  borderRadius: '10px',
  px: '17px',
  minHeight: 56,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  boxSizing: 'border-box',
} as const

/** Figma 4399:8915 — picture option row (dashed frame + inner upload well). */
const PC_ROW_DASH = '#b3ddef'
const PC_ROW_UPLOAD_WELL = '#f0f8fd'
const PC_ROW_HANDOFF_BLUE = '#008cd0'
const PC_ROW_SUBTEXT = '#a0abbf'

/** Figma 4399:8970 — "Image Display" section title (head_Bold_16). */
const PC_IMAGE_DISPLAY_TITLE_SX = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#0d1b2a',
  m: 0,
  mb: '12px',
} as const

/** Figma 4399:8972 — "Image Size" field label. */
const PC_IMAGE_SIZE_LABEL_SX = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '19.5px',
  letterSpacing: '-0.0762px',
  color: '#6b7a90',
  m: 0,
  mb: '8px',
} as const

/** Figma 4399:8973 — outer width ≈ 65.93 + 81.484 + 67.102 px; segment ratio from handoff. */
const PC_IMAGE_SIZE_CONTROL_MAX_PX = 214.52
const PC_IMAGE_SIZE_GRID_COLS = 'minmax(0, 65.93fr) minmax(0, 81.484fr) minmax(0, 67.102fr)' as const

/** Figma 4399:8973 — segmented control shell (1px #e4e9f0, 10px radius, 36px row). */
const PC_IMAGE_SIZE_TOGGLE_GROUP_SX = {
  display: 'grid',
  gridTemplateColumns: PC_IMAGE_SIZE_GRID_COLS,
  width: '100%',
  maxWidth: `${PC_IMAGE_SIZE_CONTROL_MAX_PX}px`,
  alignSelf: 'flex-start',
  border: '1px solid #e4e9f0',
  borderRadius: '10px',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '& .MuiToggleButtonGroup-grouped': { margin: 0, flex: 'none', width: '100%', maxWidth: 'none' },
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: 0,
    '&::before': { display: 'none' },
  },
  '& .MuiToggleButton-root:not(:first-of-type)': {
    borderLeft: '1px solid #e4e9f0',
  },
} as const

/** Figma 4399:8973 — segment text; selected = primary fill + white label; cell fills grid track. */
const PC_IMAGE_SIZE_TOGGLE_SX = {
  width: '100%',
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '19.5px',
  letterSpacing: '-0.0762px',
  minHeight: 36,
  maxHeight: 36,
  py: 0,
  px: 0.5,
  color: '#6b7a90',
  bgcolor: '#fff',
  '&.Mui-selected': {
    color: '#fff',
    bgcolor: PRIMARY,
    '&:hover': { bgcolor: PRIMARY },
  },
  '&:hover': {
    bgcolor: 'rgba(0, 140, 208, 0.06)',
  },
  '&.Mui-selected:hover': {
    bgcolor: PRIMARY,
  },
} as const

/** Figma 4399:8653 / 4399:8655 — 14px body, 16×12 padding, #e2e8f0 border, 10px radius, placeholder #9ca3af */
const ANSWER_OPTION_TEXT_FIELD_SX = {
  flex: 1,
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '& .MuiOutlinedInput-input': {
    py: '12px',
    px: '16px',
    fontSize: '14px',
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-input::placeholder': { color: '#9ca3af', opacity: 1 },
} as const

/** Figma 4399:8657 / 4399:8658 */
const ANSWER_OPTION_CHAR_COUNT_SX = {
  fontSize: '12px',
  lineHeight: '20px',
  color: '#9ca3af',
  whiteSpace: 'nowrap',
} as const

/** Figma 4399:8661 / 4398:7139 — section title after Special Options; vertical gap from switches uses `selection-rules-section` `mt` (avoids margin-collapse with special-options `mb`). */
const SELECTION_RULES_HEADING_SX = {
  ...DISPLAY_SETTINGS_TITLE_TEXT_SX,
  mt: 0,
  mb: 1.5,
  display: 'block',
} as const

/** Figma 4399:8664 / 4399:8669 — captions above inputs (not MUI floating labels) */
const SELECTION_RULE_FIELD_LABEL_SX = {
  display: 'block',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: '#64748b',
  mb: 0.5,
} as const

/** Figma 4399:8666 / 4399:8671 — same shell as answer options, full width in grid */
const SELECTION_RULE_TEXT_FIELD_SX = {
  width: '100%',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '& .MuiOutlinedInput-input': {
    py: '12px',
    px: '16px',
    fontSize: '14px',
    lineHeight: '20px',
  },
  '& .MuiOutlinedInput-input::placeholder': { color: '#9ca3af', opacity: 1 },
} as const

/** Figma 4399:9179 — Short Text input validation / display field labels (#6b7a90, medium 14). */
const ST_FIELD_LABEL_SX = {
  display: 'block',
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 500,
  color: '#6b7a90',
  mb: 1,
  letterSpacing: '-0.1504px',
} as const

/** Figma `4399:9152` / `4399:9157` / `4399:9194` — outlined `::placeholder` for min/max + DATE (`ST_OUTLINED_INPUT_SX`): `#a0abbf`, **not** italic. Short/Long **hint** fields use italic `::placeholder` (Figma `4399:9206` / `4399:9163`). */
export const ST_OUTLINED_INPUT_PLACEHOLDER_SX = {
  color: '#a0abbf',
  opacity: 1,
} as const

/** Figma `4399:9206` — Display Settings hint field when empty. */
const ST_SHORT_TEXT_HINT_FIELD_PLACEHOLDER = 'e.g. Type your answer here...'

/** Figma `4399:9220` / `4399:9221` — LIVE PREVIEW default when builder hint is empty. */
export const ST_SHORT_TEXT_LIVE_PREVIEW_DEFAULT_HINT = 'Type your answer here'

/** Long Text live preview `TextField` `::placeholder` (hint inside field): Inter Medium 14, `#a0abbf`, **top-left**, **`not-italic`** (product; Figma `4399:9222` was centered). */
export const LONG_TEXT_LIVE_PREVIEW_PLACEHOLDER_SX = {
  ...ST_OUTLINED_INPUT_PLACEHOLDER_SX,
  fontWeight: 500,
  lineHeight: '21px',
  letterSpacing: '-0.1504px',
  textAlign: 'left',
} as const

/** Figma 4399:9194 — 44px row, #e4e9f0 outline; placeholder see `ST_OUTLINED_INPUT_PLACEHOLDER_SX`. */
const ST_OUTLINED_INPUT_SX = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    minHeight: 44,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e9f0' },
  },
  '& .MuiOutlinedInput-input': {
    py: '11px',
    px: '14px',
    fontSize: '14px',
    lineHeight: 'normal',
  },
  '& .MuiOutlinedInput-input::placeholder': ST_OUTLINED_INPUT_PLACEHOLDER_SX,
} as const

/** Short Text hint `TextField`: same shell as `ST_OUTLINED_INPUT_SX`, italic `::placeholder` (Figma `4399:9206`). */
const ST_SHORT_TEXT_HINT_FIELD_SX = {
  ...ST_OUTLINED_INPUT_SX,
  '& .MuiOutlinedInput-input::placeholder': {
    ...ST_OUTLINED_INPUT_PLACEHOLDER_SX,
    fontStyle: 'italic',
  },
} as const

/** Figma `4399:9163` — Long Text Display Settings hint field when empty. */
const ST_LONG_TEXT_HINT_FIELD_PLACEHOLDER = 'e.g. Please Describe in details'

/** Long Text hint `TextField`: italic `::placeholder` (Figma `4399:9163`); caption **9161** is roman (`ST_FIELD_LABEL_SX`). */
const ST_LONG_TEXT_HINT_FIELD_SX = {
  ...ST_OUTLINED_INPUT_SX,
  '& .MuiOutlinedInput-input::placeholder': {
    ...ST_OUTLINED_INPUT_PLACEHOLDER_SX,
    fontStyle: 'italic',
  },
} as const

/** Figma 4399:9462–9466 — DATE placeholder: roman caption, italic sample inside field. */
export const ST_DATE_PLACEHOLDER_FIELD_SX = {
  ...ST_OUTLINED_INPUT_SX,
  '& .MuiOutlinedInput-input::placeholder': {
    ...ST_OUTLINED_INPUT_PLACEHOLDER_SX,
    fontStyle: 'italic',
    letterSpacing: '-0.1504px',
  },
} as const

/** Figma 4399:9189 — "Input Validation" */
const ST_INPUT_VALIDATION_HEADING_SX = {
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#0d1b2a',
  letterSpacing: '-0.3125px',
  mt: 0,
  mb: 1.75,
} as const

/** Long Text **LIVE PREVIEW** default when builder hint is empty (no “e.g.” prefix). */
const ST_DEFAULT_HINT_PLACEHOLDER = 'Please Describe in details'

function SelectionRulesMinMaxRow({
  minSelection,
  maxSelection,
  onMinChange,
  onMaxChange,
  minError,
  maxError,
}: Readonly<{
  minSelection: string
  maxSelection: string
  onMinChange: (next: string) => void
  onMaxChange: (next: string) => void
  minError: boolean
  maxError: boolean
}>) {
  const baseId = useId()
  const minId = `${baseId}-min-selection`
  const maxId = `${baseId}-max-selection`
  return (
    <>
      <Typography component="h3" data-testid="selection-rules-heading" sx={SELECTION_RULES_HEADING_SX}>
        Selection Rules
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} data-testid="selection-rules-min-max-row">
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor={minId} sx={SELECTION_RULE_FIELD_LABEL_SX}>
            Min Selection
          </Typography>
          <TextField
            id={minId}
            fullWidth
            hiddenLabel
            placeholder="Eg:1"
            value={minSelection}
            onChange={(e) => onMinChange(sanitizePositiveIntegerDigits(e.target.value))}
            error={minError}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-invalid': minError }}
            sx={SELECTION_RULE_TEXT_FIELD_SX}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="label" htmlFor={maxId} sx={SELECTION_RULE_FIELD_LABEL_SX}>
            Max Selection
          </Typography>
          <TextField
            id={maxId}
            fullWidth
            hiddenLabel
            placeholder="Eg:2"
            value={maxSelection}
            onChange={(e) => onMaxChange(sanitizePositiveIntegerDigits(e.target.value))}
            error={maxError}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-invalid': maxError }}
            sx={SELECTION_RULE_TEXT_FIELD_SX}
          />
        </Box>
      </Stack>
    </>
  )
}

function AnswerOptionSectionHeader({
  testId,
  onAdd,
}: Readonly<{
  testId: string
  onAdd: () => void
}>) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 1 }} data-testid={testId}>
      <Typography
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '20px',
          letterSpacing: '-0.15px',
          color: '#0f172a',
          m: 0,
        }}
      >
        Answer Option <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
      </Typography>
      <IconButton
        aria-label="Add answer option"
        data-testid={`${testId}-add`}
        onClick={onAdd}
        size="small"
        sx={{ color: PRIMARY }}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </Stack>
  )
}

/** BFP-1156 — per-row Add Text switch state (MC / checkbox only). */
type AnswerOptionAddText = {
  checked: boolean
  disabled: boolean
  onChange: (on: boolean) => void
}

function AnswerOptionFieldRow({
  index,
  value,
  optionsLength,
  onChange,
  onRemove,
  addText,
}: Readonly<{
  index: number
  value: string
  optionsLength: number
  onChange: (next: string) => void
  onRemove: () => void
  addText?: AnswerOptionAddText
}>) {
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          placeholder={`Option ${index + 1}`}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, ANSWER_OPTION_FIELD_MAX))}
          sx={ANSWER_OPTION_TEXT_FIELD_SX}
        />
        <Typography component="span" sx={ANSWER_OPTION_CHAR_COUNT_SX} aria-live="polite">
          {value.length} / {ANSWER_OPTION_FIELD_MAX}
        </Typography>
        {addText ? (
          <AddTextSwitch
            checked={addText.checked}
            disabled={addText.disabled}
            onChange={addText.onChange}
            optionName={value.trim() || `option ${index + 1}`}
            testId={`answer-option-add-text-${index}`}
          />
        ) : null}
        <IconButton
          aria-label={`Remove option ${index + 1}`}
          disabled={optionsLength <= 2}
          onClick={onRemove}
          size="small"
        >
          <RiDeleteBinLine size={22} color="#ef4444" aria-hidden />
        </IconButton>
      </Stack>
    </Stack>
  )
}

/** Stable row keys when switching questions; regenerated per `questionId`. */
function useAnswerOptionRowKeys(
  questionId: string,
  active: boolean,
  options: readonly string[],
): readonly [string[], Dispatch<SetStateAction<string[]>>] {
  const [optRowKeys, setOptRowKeys] = useState<string[]>(() =>
    active ? options.map(() => crypto.randomUUID()) : [],
  )

  useEffect(() => {
    if (!active) {
      setOptRowKeys([])
      return
    }
    setOptRowKeys(options.map(() => crypto.randomUUID()))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset keys only when the question row changes
  }, [questionId])

  return [optRowKeys, setOptRowKeys]
}

function AnswerOptionsEditorSection({
  headerTestId,
  rowKeyPrefix,
  questionId,
  options,
  optionIds,
  optRowKeys,
  setOptRowKeys,
  onOptionsChange,
  textResponse,
  onTextResponseToggle,
  exclusiveOptionIds,
}: Readonly<{
  headerTestId: string
  rowKeyPrefix: string
  questionId: string
  options: readonly string[]
  optionIds?: readonly string[]
  optRowKeys: readonly string[]
  setOptRowKeys: Dispatch<SetStateAction<string[]>>
  onOptionsChange: (options: string[], optionIds: string[]) => void
  /** BFP-1156 — when provided, each row shows an Add Text switch. */
  textResponse?: ChoiceTextResponseConfig | null
  onTextResponseToggle?: (optionId: string, on: boolean, optionIds: string[]) => void
  /** Option ids that carry routing logic on a multi-select question. */
  exclusiveOptionIds?: ReadonlySet<string>
}>) {
  const optionsErr = useGatedBuilderFieldError(errorOptionsNeedTwoNonEmpty(options))
  const stableIds = optionIds ?? optionIdsForLabels(options, optionIds)

  const removeOptionAt = (index: number) => {
    setOptRowKeys((k) => withoutIndex(k, index))
    const nextOptions = withoutIndex(options, index)
    const nextIds = withoutIndexAt(stableIds, index)
    onOptionsChange(nextOptions, nextIds)
  }

  const addTextForRow = (index: number): AnswerOptionAddText | undefined => {
    if (!onTextResponseToggle) return undefined
    const optionId = stableIds[index] ?? ''
    const checked = isTextResponseOn(textResponse, 'option', optionId)
    return {
      checked,
      disabled: textResponse != null && !checked,
      onChange: (on) => onTextResponseToggle(optionId, on, [...stableIds]),
    }
  }

  return (
    <>
      <AnswerOptionSectionHeader
        testId={headerTestId}
        onAdd={() => {
          setOptRowKeys((k) => [...k, crypto.randomUUID()])
          onOptionsChange([...options, ''], [...stableIds, newObjectIdHex()])
        }}
      />
      <Stack spacing={1}>
        {options.map((opt, i) => (
          <Box key={optRowKeys[i] ?? `${rowKeyPrefix}-fallback-${questionId}-${i}`}>
            <AnswerOptionFieldRow
              index={i}
              value={opt}
              optionsLength={options.length}
              onChange={(next) => {
                const nextOpts = [...options]
                nextOpts[i] = next
                onOptionsChange(nextOpts, [...stableIds])
              }}
              onRemove={() => {
                removeOptionAt(i)
              }}
              addText={addTextForRow(i)}
            />
            {exclusiveOptionIds?.has(stableIds[i] ?? '') ? (
              <ExclusiveRoutingAnswerWarning
                variant="full"
                testId={`answer-option-exclusive-routing-warning-${i}`}
              />
            ) : null}
          </Box>
        ))}
      </Stack>
      {optionsErr ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {optionsErr}
        </FormHelperText>
      ) : null}
    </>
  )
}

const SPECIAL_OPTIONS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: 2,
  rowGap: 1.5,
  alignItems: 'flex-start',
  mb: 0.5,
} as const

function SpecialOptionsGrid({
  layoutTestId,
  other,
  noneOfAbove,
  onOtherChange,
  onNoneOfAboveChange,
  otherAddText,
  noneAddText,
}: Readonly<{
  layoutTestId: string
  other: boolean
  noneOfAbove: boolean
  onOtherChange: (v: boolean) => void
  onNoneOfAboveChange: (v: boolean) => void
  /** BFP-1156 — Add Text on the generated Other / None row (shown only while enabled). */
  otherAddText?: AnswerOptionAddText
  noneAddText?: AnswerOptionAddText
}>) {
  return (
    <>
      <Typography component="h3" sx={SPECIAL_OPTIONS_HEADING_SX}>
        Special Options
      </Typography>
      <Box data-testid={layoutTestId} sx={SPECIAL_OPTIONS_GRID_SX}>
        <Box sx={SPECIAL_OPTION_CELL_SX}>
          <DisplaySettingSwitch
            checked={other}
            onChange={onOtherChange}
            label="Add 'Other' option"
            formControlLabelSx={SPECIAL_OPTIONS_FORM_CONTROL_LABEL_SX}
          />
          {other && otherAddText ? (
            <AddTextSwitch {...otherAddText} optionName="Other" testId="special-option-other-add-text" />
          ) : null}
        </Box>
        <Box sx={SPECIAL_OPTION_CELL_SX}>
          <DisplaySettingSwitch
            checked={noneOfAbove}
            onChange={onNoneOfAboveChange}
            label="Add 'None of the Above'"
            formControlLabelSx={SPECIAL_OPTIONS_FORM_CONTROL_LABEL_SX}
          />
          {noneOfAbove && noneAddText ? (
            <AddTextSwitch
              {...noneAddText}
              optionName="None of the above"
              testId="special-option-none-add-text"
            />
          ) : null}
        </Box>
      </Box>
    </>
  )
}

const SPECIAL_OPTION_CELL_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,
} as const

const DISPLAY_SETTINGS_LAYOUT_SWITCH_LABEL_SX = {
  alignItems: 'center',
  flexShrink: 0,
  '& .MuiFormControlLabel-label': { pt: 0 },
} as const

const DISPLAY_SETTINGS_TWO_COL_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: 2,
  rowGap: 1,
  mb: 2,
  alignItems: 'center',
} as const

function ImageOptionsSectionHeader({ onAdd }: Readonly<{ onAdd: () => void }>) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 2, mb: 1 }}
      data-testid="picture-options-header"
    >
      <Typography
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '20px',
          letterSpacing: '-0.15px',
          color: '#0f172a',
          m: 0,
        }}
      >
        Image Options <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
      </Typography>
      <IconButton
        aria-label="Add image option"
        data-testid="picture-options-header-add"
        onClick={onAdd}
        size="small"
        sx={{ color: PRIMARY }}
      >
        <RiAddCircleLine size={24} color="currentColor" aria-hidden />
      </IconButton>
    </Stack>
  )
}

function PictureOptionFieldRow({
  index,
  row,
  optionsLength,
  uploadInputId,
  onCaptionChange,
  onRemove,
  onPickFile,
}: Readonly<{
  index: number
  row: PictureChoiceOptionRow
  optionsLength: number
  uploadInputId: string
  onCaptionChange: (next: string) => void
  onRemove: () => void
  onPickFile: (file: File | null) => void
}>) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const hasImage = row.imageUrl.trim().length > 0

  return (
    <Box
      data-testid={`picture-option-row-${index}`}
      sx={{
        border: `1.5px dashed ${PC_ROW_DASH}`,
        borderRadius: '14px',
        p: 2,
      }}
    >
      <input
        id={uploadInputId}
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null
          onPickFile(f)
          e.target.value = ''
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <IconButton
          type="button"
          aria-label={`Remove image option ${index + 1}`}
          disabled={optionsLength <= 2}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 2,
            p: 0.25,
          }}
        >
          <RiDeleteBinLine size={16} color="#ef4444" aria-hidden />
        </IconButton>
        <Box
          component="button"
          type="button"
          aria-label={`Image option ${index + 1}: click to browse or drop an image`}
          aria-describedby={hasImage ? undefined : `${uploadInputId}-upload-hint`}
          onClick={() => fileRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
          }}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            const f = e.dataTransfer.files?.[0] ?? null
            onPickFile(f)
          }}
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: 140,
            borderRadius: '10px',
            bgcolor: PC_ROW_UPLOAD_WELL,
            border: dragOver ? `2px solid ${PC_ROW_HANDOFF_BLUE}` : '2px solid transparent',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            px: 2,
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          {hasImage ? (
            <Box
              component="img"
              src={row.imageUrl}
              alt=""
              sx={{
                maxWidth: '100%',
                maxHeight: 124,
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          ) : (
            <>
              <Box
                data-testid="picture-option-upload-glyph"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: `2px solid ${PC_ROW_HANDOFF_BLUE}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RiUpload2Line size={16} color={PC_ROW_HANDOFF_BLUE} aria-hidden />
              </Box>
              <Typography
                id={`${uploadInputId}-upload-hint`}
                sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  lineHeight: '19.5px',
                  letterSpacing: '-0.0762px',
                  color: PC_ROW_HANDOFF_BLUE,
                }}
              >
                Click to browse or drag image here
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '11px',
                  lineHeight: '16.5px',
                  letterSpacing: '0.0645px',
                  color: PC_ROW_SUBTEXT,
                }}
              >
                Supports JPG, PNG, WEBP — Max {pictureChoiceMaxFileHumanMb()} MB
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <TextField
        id={`${uploadInputId}-caption`}
        fullWidth
        hiddenLabel
        placeholder="Add Caption(Optional)"
        value={row.caption}
        onChange={(e) => onCaptionChange(e.target.value.slice(0, PICTURE_CAPTION_MAX))}
        sx={{ ...ANSWER_OPTION_TEXT_FIELD_SX, mt: 1 }}
      />
      <Typography
        component="div"
        textAlign="right"
        sx={{
          mt: 1,
          fontSize: '11px',
          lineHeight: '16.5px',
          letterSpacing: '0.0645px',
          color: PC_ROW_SUBTEXT,
        }}
        aria-live="polite"
      >
        {row.caption.length} / {PICTURE_CAPTION_MAX}
      </Typography>
    </Box>
  )
}

function PcSection({
  q,
  onPatch,
  campaignId,
  instrumentId,
}: Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
  campaignId: string
  instrumentId: string
}>) {
  const baseId = useId()
  const [fileHint, setFileHint] = useState<string | null>(null)
  const showSaveValidation = useShowBuilderSaveValidation()
  if (q.config.type !== 'PICTURE_CHOICE') return null
  const d = q.config.data
  const optionsErr = gateFieldError(
    showSaveValidation,
    errorPictureOptionsNeedTwoImages(d.options),
  )
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'PICTURE_CHOICE', data: { ...d, ...patch } } })

  return (
    <>
      <ImageOptionsSectionHeader
        onAdd={() =>
          setData({
            options: [...d.options, { id: newObjectIdHex(), imageUrl: '', caption: '' }],
          })
        }
      />
      <Stack spacing={2}>
        {d.options.map((row, i) => (
          <PictureOptionFieldRow
            key={row.id}
            index={i}
            row={row}
            optionsLength={d.options.length}
            uploadInputId={`${baseId}-pic-${row.id}`}
            onCaptionChange={(next) => {
              const nextOpts = [...d.options]
              nextOpts[i] = { ...row, caption: next }
              setData({ options: nextOpts })
            }}
            onRemove={() => {
              if (d.options.length <= 2) return
              setData({ options: d.options.filter((_, j) => j !== i) })
            }}
            onPickFile={(file) => {
              void (async () => {
                if (!file) return
                setFileHint(null)
                if (file.size > env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES) {
                  setFileHint(
                    `Each image must be ${pictureChoiceMaxFileHumanMb()} MB or less (JPG, PNG, or WEBP).`,
                  )
                  return
                }
                if (!instrumentId) {
                  setFileHint('Instrument context is required to upload images')
                  return
                }
                try {
                  const url = await uploadBuilderOptionImage({
                    campaignId,
                    instrumentId,
                    optionId: row.id,
                    file,
                  })
                  const nextOpts = [...d.options]
                  nextOpts[i] = { ...row, imageUrl: url }
                  setData({ options: nextOpts })
                } catch (err) {
                  setFileHint(err instanceof Error ? err.message : 'Image upload failed')
                }
              })()
            }}
          />
        ))}
      </Stack>
      {fileHint ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {fileHint}
        </FormHelperText>
      ) : null}
      {optionsErr ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {optionsErr}
        </FormHelperText>
      ) : null}

      <Box data-testid="picture-choice-image-display-section" sx={{ mt: 3, mb: 2 }}>
        <Typography component="h3" sx={PC_IMAGE_DISPLAY_TITLE_SX}>
          Image Display
        </Typography>
        <Typography component="p" sx={PC_IMAGE_SIZE_LABEL_SX}>
          Image Size
        </Typography>
        <ToggleButtonGroup
          exclusive
          data-testid="picture-choice-image-size-segmented"
          aria-label="Picture choice image size"
          value={d.imageSize}
          onChange={(_, v) => {
            if (v) setData({ imageSize: v as PictureChoiceImageSize })
          }}
          sx={PC_IMAGE_SIZE_TOGGLE_GROUP_SX}
        >
          <ToggleButton value="small" data-testid="picture-size-small" sx={PC_IMAGE_SIZE_TOGGLE_SX}>
            Small
          </ToggleButton>
          <ToggleButton value="medium" data-testid="picture-size-medium" sx={PC_IMAGE_SIZE_TOGGLE_SX}>
            Medium
          </ToggleButton>
          <ToggleButton value="large" data-testid="picture-size-large" sx={PC_IMAGE_SIZE_TOGGLE_SX}>
            Large
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  )
}

function ItemsToRankSectionHeader({ onAdd }: Readonly<{ onAdd: () => void }>) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 2, mb: 1 }}
      data-testid="ranking-items-header"
    >
      <Stack
        direction="row"
        alignItems="center"
        component="div"
        aria-label="Items to Rank (required)"
        sx={{ gap: '4px' }}
      >
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '-0.3125px',
            color: '#0d1b2a',
            m: 0,
          }}
        >
          Items to Rank
        </Typography>
        <Typography
          component="span"
          aria-hidden
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#ef4444',
          }}
        >
          *
        </Typography>
      </Stack>
      <IconButton
        aria-label="Add item to rank"
        data-testid="ranking-items-header-add"
        onClick={onAdd}
        size="small"
        sx={{ color: PRIMARY }}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </Stack>
  )
}

function SortableRankingEditorRow({
  row,
  index,
  optionsLength,
  uploadInputId,
  onCaptionChange,
  onRemove,
  onPickFile,
}: Readonly<{
  row: PictureChoiceOptionRow
  index: number
  optionsLength: number
  uploadInputId: string
  onCaptionChange: (next: string) => void
  onRemove: () => void
  onPickFile: (file: File | null) => void
}>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as const
  const hasImg = row.imageUrl.trim().length > 0
  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid={`ranking-editor-row-${row.id}`}
      sx={{
        ...RANKING_ROW_SURFACE_SX,
        opacity: isDragging ? 0.92 : 1,
      }}
    >
      <input
        id={uploadInputId}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null
          onPickFile(file)
          e.target.value = ''
        }}
      />
      <IconButton
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder item ${index + 1}`}
        size="small"
        sx={{ color: '#cbd5e1', p: 0.5, flexShrink: 0 }}
      >
        <DragIndicatorIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: PRIMARY,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          size="small"
          hiddenLabel
          placeholder="Enter item to rank"
          value={row.caption}
          onChange={(e) => onCaptionChange(e.target.value.slice(0, RANKING_ITEM_TEXT_MAX))}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': { minHeight: 40, fontSize: '14px' },
          }}
          inputProps={{
            maxLength: RANKING_ITEM_TEXT_MAX,
            'data-testid': `ranking-caption-${row.id}`,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0.5, maxHeight: 'none', alignSelf: 'center' }}>
                <Typography
                  component="span"
                  data-testid={`ranking-caption-count-${row.id}`}
                  sx={{
                    fontSize: '11px',
                    lineHeight: '16.5px',
                    letterSpacing: '0.0645px',
                    color: PC_ROW_SUBTEXT,
                    whiteSpace: 'nowrap',
                  }}
                  aria-live="polite"
                >
                  {row.caption.length} / {RANKING_ITEM_TEXT_MAX}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box
        component="label"
        htmlFor={uploadInputId}
        sx={{
          width: 38,
          height: 38,
          borderRadius: '5px',
          bgcolor: '#f1f5f9',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        {hasImg ? (
          <Box
            component="img"
            src={row.imageUrl}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: PC_ROW_SUBTEXT }} aria-hidden />
        )}
      </Box>
      <IconButton
        type="button"
        aria-label="Remove item from rank list"
        onClick={onRemove}
        disabled={optionsLength <= 2}
        size="small"
        sx={{ color: '#64748b', flexShrink: 0 }}
      >
        <RiDeleteBinLine size={20} aria-hidden />
      </IconButton>
    </Box>
  )
}

function RaSection({
  q,
  onPatch,
  campaignId,
  instrumentId,
}: Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
  campaignId: string
  instrumentId: string
}>) {
  const baseId = useId()
  const [fileHint, setFileHint] = useState<string | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const showSaveValidation = useShowBuilderSaveValidation()
  if (q.config.type !== 'RANKING') return null
  const d = q.config.data
  const optionsErr = gateFieldError(
    showSaveValidation,
    errorRankingOptionsNeedTwoNonEmpty(d.options),
  )
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'RANKING', data: { ...d, ...patch } } })

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = d.options.findIndex((o) => o.id === String(active.id))
    const newIndex = d.options.findIndex((o) => o.id === String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setData({ options: arrayMove(d.options, oldIndex, newIndex) })
  }

  return (
    <>
      <ItemsToRankSectionHeader
        onAdd={() =>
          setData({
            options: [...d.options, { id: newObjectIdHex(), imageUrl: '', caption: '' }],
          })
        }
      />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={d.options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
          <Stack spacing={1}>
            {d.options.map((row, i) => (
              <SortableRankingEditorRow
                key={row.id}
                row={row}
                index={i}
                optionsLength={d.options.length}
                uploadInputId={`${baseId}-rank-${row.id}`}
                onCaptionChange={(next) => {
                  const nextOpts = [...d.options]
                  nextOpts[i] = { ...row, caption: next }
                  setData({ options: nextOpts })
                }}
                onRemove={() => {
                  if (d.options.length <= 2) return
                  setData({ options: d.options.filter((_, j) => j !== i) })
                }}
                onPickFile={(file) => {
                  void (async () => {
                    if (!file) return
                    setFileHint(null)
                    if (file.size > env.VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES) {
                      setFileHint(
                        `Each image must be ${pictureChoiceMaxFileHumanMb()} MB or less (JPG, PNG, or WEBP).`,
                      )
                      return
                    }
                    if (!instrumentId) {
                      setFileHint('Instrument context is required to upload images')
                      return
                    }
                    try {
                      const url = await uploadBuilderOptionImage({
                        campaignId,
                        instrumentId,
                        optionId: row.id,
                        file,
                      })
                      const nextOpts = [...d.options]
                      nextOpts[i] = { ...row, imageUrl: url }
                      setData({ options: nextOpts })
                    } catch (err) {
                      setFileHint(err instanceof Error ? err.message : 'Image upload failed')
                    }
                  })()
                }}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      {fileHint ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {fileHint}
        </FormHelperText>
      ) : null}
      {optionsErr ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {optionsErr}
        </FormHelperText>
      ) : null}
    </>
  )
}

/** Figma 4399:9407–9416 — LIVE PREVIEW rating stars (32×32, Material rounded glyphs). */
export const RATING_PREVIEW_STAR_SIZE_PX = 32

const RATING_PREVIEW_STAR_ICON_SX = {
  fontSize: RATING_PREVIEW_STAR_SIZE_PX,
  width: RATING_PREVIEW_STAR_SIZE_PX,
  height: RATING_PREVIEW_STAR_SIZE_PX,
  color: PRIMARY,
  display: 'block',
} as const

function RatingPreviewStarIcon({ filled, value }: Readonly<{ filled: boolean; value: number }>) {
  const testId = `preview-rating-star-icon-${value}`
  if (filled) {
    return <StarRoundedIcon sx={RATING_PREVIEW_STAR_ICON_SX} aria-hidden data-testid={testId} />
  }
  return <StarOutlineRoundedIcon sx={RATING_PREVIEW_STAR_ICON_SX} aria-hidden data-testid={testId} />
}

const RATING_SCALE_LABEL_SX = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  color: '#6b7a90',
  letterSpacing: '-0.1504px',
} as const

const RATING_SECTION_HEADING_SX = {
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '-0.3125px',
  color: '#0d1b2a',
} as const

const RATING_PILL_TOGGLE_SX = {
  textTransform: 'none',
  borderRadius: '10px',
  minHeight: 36,
  px: 2,
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '-0.1504px',
  border: '1px solid #e4e9f0',
  color: '#0f172a',
  '&:not(.Mui-selected) svg': { color: '#0d1b2a' },
  '&.Mui-selected svg': { color: '#fff' },
  '&.Mui-selected': {
    bgcolor: '#008cd0',
    color: '#fff',
    borderColor: '#008cd0',
  },
  '&.Mui-selected:hover': {
    bgcolor: '#008cd0',
    borderColor: '#008cd0',
  },
} as const

/** Override MUI segmented-control radii so each rating pill stays fully rounded (Figma 4399:9354). */
const RATING_TOGGLE_GROUP_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  '& .MuiToggleButtonGroup-grouped': { margin: 0 },
  '& .MuiToggleButtonGroup-firstButton, & .MuiToggleButtonGroup-middleButton': {
    borderTopRightRadius: '10px',
    borderBottomRightRadius: '10px',
  },
  '& .MuiToggleButtonGroup-lastButton, & .MuiToggleButtonGroup-middleButton': {
    marginLeft: 0,
    borderTopLeftRadius: '10px',
    borderBottomLeftRadius: '10px',
    borderLeft: '1px solid #e4e9f0',
  },
  '& .MuiToggleButtonGroup-lastButton.Mui-selected': {
    borderLeftColor: '#008cd0',
  },
} as const

function RtSection({
  q,
  onPatch,
}: Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
}>) {
  if (q.config.type !== 'RATING') return null
  const d = q.config.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'RATING', data: { ...d, ...patch } } })

  return (
    <>
      <Box sx={{ mt: 0 }}>
        <Typography
          component="h3"
          sx={{
            ...RATING_SECTION_HEADING_SX,
            mb: '14px',
          }}
        >
          Scale Settings
        </Typography>
        <Stack spacing={1}>
          <Typography sx={RATING_SCALE_LABEL_SX}>Scale</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Rating scale"
            value={d.scaleMax}
            onChange={(_, v: RatingScaleMax | null) => {
              if (v != null) setData({ scaleMax: v })
            }}
            sx={RATING_TOGGLE_GROUP_SX}
          >
            <ToggleButton value={5} sx={RATING_PILL_TOGGLE_SX} data-testid="rating-scale-5">
              1 – 5
            </ToggleButton>
            <ToggleButton value={10} sx={RATING_PILL_TOGGLE_SX} data-testid="rating-scale-10">
              1 – 10
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack spacing={1} sx={{ mt: '20px' }}>
          <Typography sx={RATING_SCALE_LABEL_SX}>Display Style</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Rating display style"
            value={d.displayStyle}
            onChange={(_, v: RatingDisplayStyle | null) => {
              if (v != null) setData({ displayStyle: v })
            }}
            sx={RATING_TOGGLE_GROUP_SX}
          >
            <ToggleButton value="stars" sx={RATING_PILL_TOGGLE_SX} data-testid="rating-style-stars">
              <Stack direction="row" alignItems="center" spacing={0.75} component="span">
                <StarBorderIcon sx={{ fontSize: 14 }} />
                <span>Stars</span>
              </Stack>
            </ToggleButton>
            <ToggleButton value="numbers" sx={RATING_PILL_TOGGLE_SX} data-testid="rating-style-numbers">
              <Stack direction="row" alignItems="center" spacing={0.75} component="span">
                <NumbersOutlinedIcon sx={{ fontSize: 14 }} />
                <span>Numbers</span>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      <Typography component="h3" sx={{ ...RATING_SECTION_HEADING_SX, mt: 3, mb: 1.5 }}>
        Labels (Optional)
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 2,
          rowGap: 1,
        }}
      >
        <Stack spacing={1}>
          <Typography sx={RATING_SCALE_LABEL_SX}>Low End Label</Typography>
          <TextField
            fullWidth
            hiddenLabel
            placeholder="e.g. Poor"
            value={d.lowLabel}
            onChange={(e) => onPatch({ config: { type: 'RATING', data: { ...d, lowLabel: e.target.value.slice(0, 100) } } })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', minHeight: 44 } }}
            inputProps={{ 'data-testid': 'rating-low-label' }}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography sx={RATING_SCALE_LABEL_SX}>High End Label</Typography>
          <TextField
            fullWidth
            hiddenLabel
            placeholder="e.g. Excellent"
            value={d.highLabel}
            onChange={(e) => onPatch({ config: { type: 'RATING', data: { ...d, highLabel: e.target.value.slice(0, 100) } } })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', minHeight: 44 } }}
            inputProps={{ 'data-testid': 'rating-high-label' }}
          />
        </Stack>
      </Box>
    </>
  )
}

const QUESTION_BUILDER_VALIDATION_WARNING_COLOR = '#b45309'
const LIKERT_SCALE_REDUCED_WARNING =
  'Scale reduced — labels beyond selected range have been removed'

const LIKERT_STEPPER_BTN_SX = {
  width: 57,
  minWidth: 57,
  height: 46,
  p: 0,
  borderRadius: 0,
  bgcolor: '#f4f4f7',
  color: 'text.primary',
  '&:hover': { bgcolor: '#ececf1' },
  '&.Mui-disabled': { color: 'text.disabled', bgcolor: '#f4f4f7' },
} as const

function LikertPointCountStepper({
  value,
  draft,
  onDraftChange,
  onCommit,
  onDecrement,
  onIncrement,
}: Readonly<{
  value: number
  draft: string | null
  onDraftChange: (raw: string) => void
  onCommit: () => void
  onDecrement: () => void
  onIncrement: () => void
}>) {
  const display = draft ?? String(value)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: 171,
        height: 46,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
      data-testid="likert-point-count-stepper"
    >
      <IconButton
        type="button"
        aria-label="Decrease number of scale points"
        data-testid="likert-point-count-decrement"
        disabled={value <= LIKERT_POINT_COUNT_MIN}
        onClick={onDecrement}
        sx={{
          ...LIKERT_STEPPER_BTN_SX,
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <RiSubtractFill size={16} aria-hidden />
      </IconButton>
      <Box
        component="input"
        aria-label="Number of scale points"
        data-testid="likert-point-count-input"
        inputMode="numeric"
        value={display}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onDraftChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onCommit()
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: 'none',
          textAlign: 'center',
          fontSize: '14px',
          lineHeight: '20px',
          color: 'text.primary',
          bgcolor: 'background.paper',
          px: 0.5,
        }}
      />
      <IconButton
        type="button"
        aria-label="Increase number of scale points"
        data-testid="likert-point-count-increment"
        disabled={value >= LIKERT_POINT_COUNT_MAX}
        onClick={onIncrement}
        sx={{
          ...LIKERT_STEPPER_BTN_SX,
          borderLeft: '1px solid',
          borderColor: 'divider',
        }}
      >
        <RiAddFill size={16} aria-hidden />
      </IconButton>
    </Box>
  )
}

function LkSection({
  q,
  onPatch,
}: Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
}>) {
  const baseId = useId()
  const showSaveValidation = useShowBuilderSaveValidation()
  const skipInitialPointCountEffectRef = useRef(true)
  const [peakPointCount, setPeakPointCount] = useState<number | null>(null)
  const [countDraft, setCountDraft] = useState<string | null>(null)
  const pointCount = q.config.type === 'LIKERT' ? q.config.data.pointCount : 0

  useEffect(() => {
    if (q.config.type !== 'LIKERT') return
    if (skipInitialPointCountEffectRef.current) {
      skipInitialPointCountEffectRef.current = false
      return
    }
    setPeakPointCount((prev) => {
      if (prev === null) return pointCount
      if (pointCount > prev) return pointCount
      return prev
    })
  }, [q.config.type, pointCount])

  const labelLen = q.config.type === 'LIKERT' ? q.config.data.labels.length : 0
  const labelRowKeys = useMemo(
    () => Array.from({ length: labelLen }, () => crypto.randomUUID()),
    [q.id, labelLen, q.config.type],
  )
  if (q.config.type !== 'LIKERT') return null
  const d = q.config.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'LIKERT', data: { ...d, ...patch } } })

  const applyPointCount = (nextRaw: number) => {
    const next = clampLikertPointCount(nextRaw)
    if (next === d.pointCount) return
    setData({
      pointCount: next,
      labels: likertLabelsForPointCount(next, d.labels, d.pointCount),
    })
  }

  const commitCountDraft = () => {
    if (countDraft == null) return
    const parsed = Number(countDraft.replaceAll(/\D/g, ''))
    applyPointCount(Number.isFinite(parsed) ? parsed : d.pointCount)
    setCountDraft(null)
  }

  const showLikertScaleReducedWarning =
    peakPointCount !== null && d.pointCount < peakPointCount

  return (
    <>
      <Box sx={{ mt: 0 }}>
        <Typography component="h3" sx={{ ...RATING_SECTION_HEADING_SX, mb: '8px' }}>
          Scale Configuration{' '}
          <Box component="span" sx={{ color: 'error.main' }}>
            *
          </Box>
        </Typography>
        <Typography
          sx={{
            display: 'block',
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: 400,
            color: 'text.secondary',
            mb: '16px',
          }}
        >
          Choose how many scale points you want to use for this Likert scale.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '21px',
              color: 'text.primary',
            }}
          >
            Number of scale points
          </Typography>
          <LikertPointCountStepper
            value={d.pointCount}
            draft={countDraft}
            onDraftChange={(raw) => setCountDraft(raw.replaceAll(/\D/g, '').slice(0, 2))}
            onCommit={commitCountDraft}
            onDecrement={() => {
              setCountDraft(null)
              applyPointCount(d.pointCount - 1)
            }}
            onIncrement={() => {
              setCountDraft(null)
              applyPointCount(d.pointCount + 1)
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '21px',
              fontWeight: 400,
              color: 'text.secondary',
            }}
          >
            Allowed range {LIKERT_POINT_COUNT_MIN}-{LIKERT_POINT_COUNT_MAX}
          </Typography>
        </Stack>
        {showLikertScaleReducedWarning ? (
          <Box
            data-testid="likert-scale-reduced-warning"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              py: 0.5,
              mt: 1,
            }}
          >
            <WarningAmberOutlinedIcon
              data-testid="WarningAmberOutlinedIcon"
              sx={{
                color: QUESTION_BUILDER_VALIDATION_WARNING_COLOR,
                fontSize: 14,
                mt: '2px',
                flexShrink: 0,
              }}
              aria-hidden
            />
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                color: QUESTION_BUILDER_VALIDATION_WARNING_COLOR,
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '19.5px',
                wordBreak: 'break-word',
              }}
            >
              {LIKERT_SCALE_REDUCED_WARNING}
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Typography component="h3" sx={{ ...RATING_SECTION_HEADING_SX, mt: 3, mb: 0.5 }}>
        Scale Labels{' '}
        <Box component="span" sx={{ color: 'error.main' }}>
          *
        </Box>
      </Typography>
      <Typography
        sx={{
          display: 'block',
          fontSize: '14px',
          lineHeight: '21px',
          fontWeight: 400,
          color: 'text.secondary',
          mb: 2,
        }}
      >
        Enter a label for each scale point. These labels will be shown to respondents.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-start',
        }}
      >
        {d.labels.map((lab, i) => {
          const fieldId = `${baseId}-lk-label-${i}`
          const labelErr = gateFieldError(showSaveValidation, lab.trim() ? null : errorLikertScaleLabelRequired(i + 1))
          return (
            <Stack
              key={labelRowKeys[i] ?? fieldId}
              spacing={0.75}
              sx={{
                flex: '1 1 168px',
                minWidth: { xs: '100%', sm: 156 },
                maxWidth: { xs: '100%', sm: 252 },
                alignItems: 'stretch',
              }}
            >
              <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#a0abbf', alignSelf: 'center' }}>
                {i + 1}
              </Typography>
              <TextField
                id={fieldId}
                fullWidth
                hiddenLabel
                placeholder={`Point ${i + 1}`}
                value={lab}
                error={Boolean(labelErr)}
                onChange={(e) => {
                  const next = [...d.labels]
                  next[i] = e.target.value.slice(0, LIKERT_LABEL_MAX)
                  setData({ labels: next })
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', minHeight: 44 } }}
                inputProps={{ 'data-testid': `likert-point-label-${i}`, maxLength: LIKERT_LABEL_MAX }}
              />
              {labelErr ? (
                <FormHelperText error sx={{ mx: 0, mt: 0 }} data-testid={`likert-point-label-error-${i}`}>
                  {labelErr}
                </FormHelperText>
              ) : null}
              <Typography variant="caption" sx={{ color: '#a0abbf' }}>
                {lab.length} / {LIKERT_LABEL_MAX}
              </Typography>
            </Stack>
          )
        })}
      </Box>

      <Typography sx={{ ...DISPLAY_SETTINGS_HEADING_SX, mt: 3 }}>Display Settings</Typography>
      <DisplaySettingSwitch
        checked={d.layoutHorizontal}
        onChange={(v) => setData({ layoutHorizontal: v })}
        label="Horizontal layout"
      />
    </>
  )
}

const SLIDER_SCALE_REDUCED_WARNING = 'Scale reduced — please review your configuration'

function SlSection({
  q,
  onPatch,
}: Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
}>) {
  const baseId = useId()
  const showSaveValidation = useShowBuilderSaveValidation()
  const skipInitialRangeEffectRef = useRef(true)
  const [peakSpan, setPeakSpan] = useState<number | null>(null)
  const sliderMinValue = q.config.type === 'SLIDER' ? q.config.data.minValue : ''
  const sliderMaxValue = q.config.type === 'SLIDER' ? q.config.data.maxValue : ''
  const currentSpan = sliderInclusiveSpan(sliderMinValue, sliderMaxValue)

  useEffect(() => {
    if (q.config.type !== 'SLIDER') return
    if (skipInitialRangeEffectRef.current) {
      skipInitialRangeEffectRef.current = false
      return
    }
    setPeakSpan((prev) => {
      if (prev === null) return currentSpan
      if (currentSpan > prev) return currentSpan
      return prev
    })
  }, [q.config.type, sliderMinValue, sliderMaxValue, currentSpan])

  if (q.config.type !== 'SLIDER') return null
  const d = q.config.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'SLIDER', data: { ...d, ...patch } } })
  const rangeErr = gateFieldError(showSaveValidation, sliderRangeValidationMessage(d))

  const showScaleReducedWarning = peakSpan !== null && currentSpan < peakSpan

  return (
    <>
      <Box sx={{ mt: 0 }}>
        <Typography component="h3" sx={{ ...RATING_SECTION_HEADING_SX, mb: '14px' }}>
          Range Settings <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Stack spacing={1}>
            <Typography sx={RATING_SCALE_LABEL_SX}>Minimum Value</Typography>
            <TextField
              id={`${baseId}-sl-min`}
              fullWidth
              hiddenLabel
              placeholder="0"
              value={d.minValue}
              onChange={(e) => setData({ minValue: sanitizeSliderMinFieldInput(e.target.value) })}
              sx={SLIDER_RANGE_TEXTFIELD_OUTLINED_SX}
              inputProps={{ 'data-testid': 'slider-min-value', inputMode: 'numeric' as const }}
              InputProps={{
                endAdornment: (
                  <SliderNumericStepperAdornment
                    ariaLabelUp="Increase minimum value"
                    ariaLabelDown="Decrease minimum value"
                    testIdUp="slider-min-step-up"
                    testIdDown="slider-min-step-down"
                    onIncrement={() => setData({ minValue: bumpSliderMinString(d.minValue, 1) })}
                    onDecrement={() => setData({ minValue: bumpSliderMinString(d.minValue, -1) })}
                  />
                ),
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography sx={RATING_SCALE_LABEL_SX}>Maximum Value</Typography>
            <TextField
              id={`${baseId}-sl-max`}
              fullWidth
              hiddenLabel
              placeholder="10"
              value={d.maxValue}
              onChange={(e) => setData({ maxValue: sanitizeSliderMaxFieldInput(e.target.value) })}
              sx={SLIDER_RANGE_TEXTFIELD_OUTLINED_SX}
              inputProps={{ 'data-testid': 'slider-max-value', inputMode: 'numeric' as const }}
              InputProps={{
                endAdornment: (
                  <SliderNumericStepperAdornment
                    ariaLabelUp="Increase maximum value"
                    ariaLabelDown="Decrease maximum value"
                    testIdUp="slider-max-step-up"
                    testIdDown="slider-max-step-down"
                    onIncrement={() => setData({ maxValue: bumpSliderMaxString(d.maxValue, 1) })}
                    onDecrement={() => setData({ maxValue: bumpSliderMaxString(d.maxValue, -1) })}
                  />
                ),
              }}
            />
            {showScaleReducedWarning ? (
              <Box
                data-testid="slider-scale-reduced-warning"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  py: 0.5,
                }}
              >
                <WarningAmberOutlinedIcon
                  sx={{
                    color: QUESTION_BUILDER_VALIDATION_WARNING_COLOR,
                    fontSize: 14,
                    mt: '2px',
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: QUESTION_BUILDER_VALIDATION_WARNING_COLOR,
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '19.5px',
                    wordBreak: 'break-word',
                  }}
                >
                  {SLIDER_SCALE_REDUCED_WARNING}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Box>

        <Stack spacing={1} sx={{ mt: 2, width: '100%', alignSelf: 'stretch' }} data-testid="slider-step-size-row">
          <Typography sx={RATING_SCALE_LABEL_SX}>Step Size</Typography>
          <TextField
            id={`${baseId}-sl-step`}
            fullWidth
            hiddenLabel
            placeholder="1"
            value={d.step}
            onChange={(e) =>
              setData({ step: sanitizePositiveIntegerDigits(e.target.value, 8) })
            }
            data-testid="slider-step-field"
            sx={{
              width: '100%',
              maxWidth: 'none',
              ...SLIDER_RANGE_TEXTFIELD_OUTLINED_SX,
            }}
            inputProps={{ 'data-testid': 'slider-step', inputMode: 'numeric' as const }}
            InputProps={{
              endAdornment: (
                <SliderNumericStepperAdornment
                  ariaLabelUp="Increase step size"
                  ariaLabelDown="Decrease step size"
                  testIdUp="slider-step-step-up"
                  testIdDown="slider-step-step-down"
                  onIncrement={() => setData({ step: bumpSliderStepString(d.step, 1) })}
                  onDecrement={() => setData({ step: bumpSliderStepString(d.step, -1) })}
                />
              ),
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 400,
              color: '#6b7a90',
            }}
          >
            How much the slider moves per increment
          </Typography>
        </Stack>
      </Box>

      <Typography component="h3" sx={{ ...RATING_SECTION_HEADING_SX, mt: 3, mb: 1.5 }}>
        Labels (Optional)
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 2,
          rowGap: 1,
        }}
      >
        <Stack spacing={1}>
          <Typography sx={RATING_SCALE_LABEL_SX}>Label at minimum end</Typography>
          <TextField
            fullWidth
            hiddenLabel
            placeholder="e.g. Not Likely"
            value={d.lowLabel}
            onChange={(e) => setData({ lowLabel: e.target.value.slice(0, SLIDER_END_LABEL_MAX) })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', minHeight: 44 } }}
            inputProps={{ 'data-testid': 'slider-low-label', maxLength: SLIDER_END_LABEL_MAX }}
          />
        </Stack>
        <Stack spacing={1}>
          <Typography sx={RATING_SCALE_LABEL_SX}>Label at maximum end</Typography>
          <TextField
            fullWidth
            hiddenLabel
            placeholder="e.g. Very Likely"
            value={d.highLabel}
            onChange={(e) => setData({ highLabel: e.target.value.slice(0, SLIDER_END_LABEL_MAX) })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', minHeight: 44 } }}
            inputProps={{ 'data-testid': 'slider-high-label', maxLength: SLIDER_END_LABEL_MAX }}
          />
        </Stack>
      </Box>
      {rangeErr ? (
        <FormHelperText error sx={{ mx: 0, mt: 1 }}>
          {rangeErr}
        </FormHelperText>
      ) : null}
    </>
  )
}

function DisplaySettingSwitch({
  checked,
  onChange,
  label,
  formControlLabelSx,
  testId,
}: Readonly<{
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  /** Merged onto default display-row styles (e.g. header row alignment). */
  formControlLabelSx?: Record<string, unknown>
  testId?: string
}>) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={(_, v) => onChange(v)}
          sx={DISPLAY_SWITCH_SX}
          inputProps={{
            'aria-label': label,
            ...(testId ? { 'data-testid': testId } : {}),
          }}
        />
      }
      label={label}
      sx={formControlLabelSx ? { ...DISPLAY_FORM_CONTROL_LABEL_SX, ...formControlLabelSx } : DISPLAY_FORM_CONTROL_LABEL_SX}
    />
  )
}


/** Figma 4398:8067 / 4398:8070 — default toolbar icon chip */
const FORMAT_TOOLBAR_ICON_BTN_SX = {
  border: '1px solid #e4e9f0',
  borderRadius: '4px',
  width: 28,
  height: 28,
  color: '#0f172a',
} as const

const FORMAT_TOOLBAR_ICON_BTN_ACTIVE_SX = {
  ...FORMAT_TOOLBAR_ICON_BTN_SX,
  bgcolor: PRIMARY_MUTED,
  borderColor: PRIMARY,
  color: PRIMARY,
} as const

const HIDE_QUESTION_TOOLTIP =
  'Still shown to responders. Hidden from dashboards and reports.'

/** Compact header-row switches — same 36px row as the bookmark chip. */
const QUESTION_HEADER_SWITCH_SX = {
  ml: 0,
  mr: 0,
  alignItems: 'center',
  gap: '8px',
  minHeight: 36,
  '& .MuiSwitch-root': { ml: 0 },
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: '#64748b',
    pt: 0,
    whiteSpace: 'nowrap',
  },
} as const

type Props = Readonly<{
  q: BuilderQuestion
  onPatch: (patch: QuestionBuilderSurfacePatch) => void
  onImmediatePersist?: () => void
  /** When true, show inline save-validation errors for this question only (US-IB-03 / BR-03). */
  showValidationErrors?: boolean
  /** Updates every DATE question when the instrument-wide display format changes (Figma 4399:9456). */
  onInstrumentDateFormatChange?: (format: DateInputDisplayFormat) => void
  /** Required for picture-choice / ranking image uploads to MinIO. */
  campaignId?: string
  instrumentId?: string
  /** BFP-601 — Admin/Editor bookmark save to Question Bank. */
  showQuestionBankBookmark?: boolean
  questionBankBookmarkFilled?: boolean
  questionBankEntries?: readonly IQuestionBankEntrySummary[]
  onQuestionBankSaved?: (entry: IQuestionBankEntryDetail) => void
  onQuestionBankSaveValidation?: () => void
  onProfileChange?: (patch: Partial<BuilderQuestion>) => void
  onUseProfileBankEntry?: (entry: IQuestionBankEntryDetail, link: ProfileLink) => void
  /** Option ids on this question referenced by routing rules (exclusive on multi-select). */
  routingOptionIds?: ReadonlySet<string>
}>

function questionConfigPanelTitleColor(cfg: BuilderQuestion['config']): string {
  if (
    cfg.type === 'RANKING' ||
    cfg.type === 'RATING' ||
    cfg.type === 'DATE' ||
    cfg.type === 'LIKERT' ||
    cfg.type === 'SLIDER'
  ) {
    return '#0f1729'
  }
  return '#0f172a'
}

const QUESTION_CONFIG_PANEL_TITLES: Record<BuilderQuestion['config']['type'], string> = {
  MULTIPLE_CHOICE: 'Multiple Choice',
  PICTURE_CHOICE: 'Picture Choice',
  RANKING: 'Ranking',
  RATING: 'Rating',
  LIKERT: 'Likert Scale',
  SLIDER: 'Slider',
  YES_NO: 'Yes / No Question',
  CHECKBOX: 'Checkbox',
  DROPDOWN: 'Dropdown',
  SHORT_TEXT: 'Short Text',
  LONG_TEXT: 'Long Text',
  DATE: 'Date',
  VIDEO: 'Video',
}

function questionConfigPanelTitle(cfg: BuilderQuestion['config']): string {
  return QUESTION_CONFIG_PANEL_TITLES[cfg.type]
}

type QuestionConfigMarkdownFieldsProps = Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  questionInputRef: RefObject<HTMLInputElement | null>
  descriptionInputRef: RefObject<HTMLTextAreaElement | null>
  questionBoldOn: boolean
  questionItalicOn: boolean
  descBoldOn: boolean
  descItalicOn: boolean
  questionTextHelperId: string
  questionTextError: string | null
}>

function QuestionConfigMarkdownFields({
  q,
  onPatch,
  questionInputRef,
  descriptionInputRef,
  questionBoldOn,
  questionItalicOn,
  descBoldOn,
  descItalicOn,
  questionTextHelperId,
  questionTextError,
}: QuestionConfigMarkdownFieldsProps) {
  const qTextFmt = q.questionTextFormat ?? defaultBuilderTextFormatFlags
  const descFmt = q.descriptionFormat ?? defaultBuilderTextFormatFlags

  return (
    <>
      <Typography sx={{ fontWeight: 700, fontSize: '16px', mb: 1, color: 'text.primary' }}>
        {q.config.type === 'VIDEO' ? 'Question Heading' : 'Question text'}{' '}
        <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <Box>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <IconButton
            size="small"
            aria-label="Bold"
            aria-pressed={questionBoldOn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onPatch({
                questionTextFormat: {
                  bold: !qTextFmt.bold,
                  italic: qTextFmt.italic,
                },
              })
              queueMicrotask(() => questionInputRef.current?.focus())
            }}
            sx={questionBoldOn ? FORMAT_TOOLBAR_ICON_BTN_ACTIVE_SX : FORMAT_TOOLBAR_ICON_BTN_SX}
          >
            <FormatBoldIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Italic"
            aria-pressed={questionItalicOn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onPatch({
                questionTextFormat: {
                  bold: qTextFmt.bold,
                  italic: !qTextFmt.italic,
                },
              })
              queueMicrotask(() => questionInputRef.current?.focus())
            }}
            sx={questionItalicOn ? FORMAT_TOOLBAR_ICON_BTN_ACTIVE_SX : FORMAT_TOOLBAR_ICON_BTN_SX}
          >
            <FormatItalicIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
        <TextField
          inputRef={questionInputRef}
          fullWidth
          placeholder="Enter your question"
          value={q.questionText}
          onChange={(e) =>
            onPatch({ questionText: e.target.value.slice(0, BUILDER_QUESTION_TEXT_MAX) })
          }
          inputProps={{
            maxLength: BUILDER_QUESTION_TEXT_MAX,
            'aria-describedby': questionTextHelperId,
            'aria-invalid': questionTextError ? true : undefined,
          }}
          error={Boolean(questionTextError)}
          helperText={questionTextError ?? `${q.questionText.length} / ${BUILDER_QUESTION_TEXT_MAX}`}
          FormHelperTextProps={{ id: questionTextHelperId }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: '16px', mt: 2, mb: 1, color: '#0f172a' }}>Description</Typography>
      <Box>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <IconButton
            size="small"
            aria-label="Bold description"
            aria-pressed={descBoldOn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onPatch({
                descriptionFormat: {
                  bold: !descFmt.bold,
                  italic: descFmt.italic,
                },
              })
              queueMicrotask(() => descriptionInputRef.current?.focus())
            }}
            sx={descBoldOn ? FORMAT_TOOLBAR_ICON_BTN_ACTIVE_SX : FORMAT_TOOLBAR_ICON_BTN_SX}
          >
            <FormatBoldIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Italic description"
            aria-pressed={descItalicOn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onPatch({
                descriptionFormat: {
                  bold: descFmt.bold,
                  italic: !descFmt.italic,
                },
              })
              queueMicrotask(() => descriptionInputRef.current?.focus())
            }}
            sx={descItalicOn ? FORMAT_TOOLBAR_ICON_BTN_ACTIVE_SX : FORMAT_TOOLBAR_ICON_BTN_SX}
          >
            <FormatItalicIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
        <TextField
          inputRef={descriptionInputRef}
          fullWidth
          multiline
          minRows={4}
          placeholder="Add additional context"
          value={q.description}
          onChange={(e) => onPatch({ description: e.target.value.slice(0, 500) })}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', color: '#9ca3af', mt: 0.5 }}>
          {q.description.length} / 500
        </Typography>
      </Box>
    </>
  )
}

export function QuestionConfigPanel(props: Props) {
  const videoUrl = props.q.config.type === 'VIDEO' ? props.q.config.data.videoUrl : null
  return (
    <BuilderSaveValidationContext.Provider value={props.showValidationErrors ?? false}>
      <RoutingOptionIdsContext.Provider value={props.routingOptionIds ?? EMPTY_ROUTING_OPTION_IDS}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
          <QuestionConfigPanelContent {...props} />
          {videoUrl == null || props.q.config.type !== 'VIDEO' ? null : (
            <>
              <VideoPreviewCard videoUrl={videoUrl}>
                <TextAnswerLivePreviewBlock isLong={false} data={props.q.config.data} />
              </VideoPreviewCard>
              <VideoGuidelinesPanels />
            </>
          )}
        </Box>
      </RoutingOptionIdsContext.Provider>
    </BuilderSaveValidationContext.Provider>
  )
}

function QuestionConfigPanelContent({
  q,
  onPatch,
  onImmediatePersist,
  onInstrumentDateFormatChange,
  campaignId = '',
  instrumentId = '',
  showQuestionBankBookmark = false,
  questionBankBookmarkFilled = false,
  questionBankEntries = [],
  onQuestionBankSaved,
  onQuestionBankSaveValidation,
  onProfileChange,
  onUseProfileBankEntry,
}: Props) {
  const questionInputRef = useRef<HTMLInputElement | null>(null)
  const descriptionInputRef = useRef<HTMLTextAreaElement | null>(null)
  const qTextFmt = q.questionTextFormat ?? defaultBuilderTextFormatFlags
  const descTextFmt = q.descriptionFormat ?? defaultBuilderTextFormatFlags
  const questionBoldOn = qTextFmt.bold
  const questionItalicOn = qTextFmt.italic
  const descBoldOn = descTextFmt.bold
  const descItalicOn = descTextFmt.italic
  const questionTextHelperId = useId()
  const questionHeadingError = errorQuestionHeadingRequired(q.questionText)
  const questionBodyError = errorQuestionTextRequired(q.questionText)
  const questionTextError = useGatedBuilderFieldError(
    q.config.type === 'VIDEO' ? questionHeadingError : questionBodyError,
  )

  useLayoutEffect(() => {
    const p: QuestionBuilderSurfacePatch = {}
    const qs = splitWholeFieldMarkdown(q.questionText)
    if (qs.plain !== q.questionText) {
      p.questionText = qs.plain
      p.questionTextFormat = qs.fmt
    } else if ((qs.fmt.bold || qs.fmt.italic) && q.questionTextFormat === undefined) {
      p.questionTextFormat = qs.fmt
    }
    const ds = splitWholeFieldMarkdown(q.description)
    if (ds.plain !== q.description) {
      p.description = ds.plain
      p.descriptionFormat = ds.fmt
    } else if ((ds.fmt.bold || ds.fmt.italic) && q.descriptionFormat === undefined) {
      p.descriptionFormat = ds.fmt
    }
    if (Object.keys(p).length > 0) {
      onPatch(p)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- normalize legacy whole-field markdown once per question row
  }, [q.id])

  return (
    <Box
      sx={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '10px',
        bgcolor: '#fff',
        p: '25px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '28px',
            color: questionConfigPanelTitleColor(q.config),
            minWidth: 0,
            pr: 1,
          }}
        >
          {questionConfigPanelTitle(q.config)}
        </Typography>
        <QuestionConfigHeaderActions
          q={q}
          onPatch={onPatch}
          onImmediatePersist={onImmediatePersist}
          showQuestionBankBookmark={showQuestionBankBookmark}
          questionBankBookmarkFilled={questionBankBookmarkFilled}
          questionBankEntries={questionBankEntries}
          onQuestionBankSaved={onQuestionBankSaved}
          onQuestionBankSaveValidation={onQuestionBankSaveValidation}
          onProfileChange={onProfileChange}
          onUseProfileBankEntry={onUseProfileBankEntry}
        />
      </Stack>

      {q.fromTemplate ? (
        <Box sx={{ mb: 1.5 }}>
          <QuestionFromTemplateBadge />
        </Box>
      ) : null}

      <QuestionConfigMarkdownFields
        q={q}
        onPatch={onPatch}
        questionInputRef={questionInputRef}
        descriptionInputRef={descriptionInputRef}
        questionBoldOn={questionBoldOn}
        questionItalicOn={questionItalicOn}
        descBoldOn={descBoldOn}
        descItalicOn={descItalicOn}
        questionTextHelperId={questionTextHelperId}
        questionTextError={questionTextError}
      />

      <QuestionTypeConfigSections
        q={q}
        onPatch={onPatch}
        onInstrumentDateFormatChange={onInstrumentDateFormatChange}
        campaignId={campaignId}
        instrumentId={instrumentId}
      />
    </Box>
  )
}

function QuestionConfigHeaderActions({
  q,
  onPatch,
  onImmediatePersist,
  showQuestionBankBookmark,
  questionBankBookmarkFilled,
  questionBankEntries,
  onQuestionBankSaved,
  onQuestionBankSaveValidation,
  onProfileChange,
  onUseProfileBankEntry,
}: Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  onImmediatePersist?: () => void
  showQuestionBankBookmark: boolean
  questionBankBookmarkFilled: boolean
  questionBankEntries: readonly IQuestionBankEntrySummary[]
  onQuestionBankSaved?: (entry: IQuestionBankEntryDetail) => void
  onQuestionBankSaveValidation?: () => void
  onProfileChange?: (patch: Partial<BuilderQuestion>) => void
  onUseProfileBankEntry?: (entry: IQuestionBankEntryDetail, link: ProfileLink) => void
}>) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      data-testid="question-header-actions"
      sx={{ gap: '12px', flexShrink: 0, minHeight: 36, flexWrap: 'nowrap' }}
    >
      {showQuestionBankBookmark ? (
        <QuestionBankBookmarkButton
          question={q}
          isFilled={questionBankBookmarkFilled}
          bankEntries={questionBankEntries}
          onSaved={(entry) => onQuestionBankSaved?.(entry)}
          onQuestionTextValidationRequested={onQuestionBankSaveValidation}
          locked={q.isProfileUpdate === true}
        />
      ) : null}
      {showQuestionBankBookmark && onProfileChange && onUseProfileBankEntry ? (
        <ProfileUpdateQuestionButton
          question={q}
          onChange={onProfileChange}
          onUseBankEntry={onUseProfileBankEntry}
        />
      ) : null}
      {showQuestionBankBookmark ? (
        <Box aria-hidden sx={{ width: '1px', height: 22, bgcolor: '#e2e8f0', flexShrink: 0 }} />
      ) : null}
      <Stack direction="row" alignItems="center" sx={{ gap: '20px', flexShrink: 0, minHeight: 36 }}>
        {showQuestionBankBookmark ? (
          <HideQuestionToolbarControl
            hidden={q.hidden === true}
            onPatch={onPatch}
          />
        ) : null}
        <DisplaySettingSwitch
          checked={q.mandatory}
          formControlLabelSx={QUESTION_HEADER_SWITCH_SX}
          onChange={(v) => {
            onPatch({ mandatory: v })
            onImmediatePersist?.()
          }}
          label="Required Question"
        />
      </Stack>
    </Stack>
  )
}

function HideQuestionToolbarControl({
  hidden,
  onPatch,
}: Readonly<{
  hidden: boolean
  onPatch: Props['onPatch']
}>) {
  const [hideConfirmOpen, setHideConfirmOpen] = useState(false)
  return (
    <>
      <HideQuestionDialog
        open={hideConfirmOpen}
        onCancel={() => setHideConfirmOpen(false)}
        onConfirm={() => {
          setHideConfirmOpen(false)
          onPatch({ hidden: true })
        }}
      />
      <Tooltip title={HIDE_QUESTION_TOOLTIP} placement="bottom">
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 36 }}>
          <DisplaySettingSwitch
            checked={hidden || hideConfirmOpen}
            testId="hide-question-toggle"
            label="Hide Question"
            formControlLabelSx={QUESTION_HEADER_SWITCH_SX}
            onChange={(value) => {
              if (value) {
                setHideConfirmOpen(true)
                return
              }
              setHideConfirmOpen(false)
              onPatch({ hidden: false })
            }}
          />
        </Box>
      </Tooltip>
    </>
  )
}

function QuestionTypeConfigSections({
  q,
  onPatch,
  onInstrumentDateFormatChange,
  campaignId,
  instrumentId,
}: Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  onInstrumentDateFormatChange: Props['onInstrumentDateFormatChange']
  campaignId: string
  instrumentId: string
}>) {
  return (
    <>
      <QuestionChoiceTypeSections q={q} onPatch={onPatch} campaignId={campaignId} instrumentId={instrumentId} />
      <QuestionScaleAndMediaTypeSections
        q={q}
        onPatch={onPatch}
        onInstrumentDateFormatChange={onInstrumentDateFormatChange}
        campaignId={campaignId}
        instrumentId={instrumentId}
      />
    </>
  )
}

function QuestionChoiceTypeSections({
  q,
  onPatch,
  campaignId,
  instrumentId,
}: Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  campaignId: string
  instrumentId: string
}>) {
  const type = q.config.type
  return (
    <>
      {type === 'MULTIPLE_CHOICE' || type === 'CHECKBOX' ? (
        <McCbOptionsSection q={q} onPatch={onPatch} />
      ) : null}
      {type === 'PICTURE_CHOICE' ? (
        <PcSection q={q} onPatch={onPatch} campaignId={campaignId} instrumentId={instrumentId} />
      ) : null}
      {type === 'RANKING' ? (
        <RaSection q={q} onPatch={onPatch} campaignId={campaignId} instrumentId={instrumentId} />
      ) : null}
      {type === 'YES_NO' ? <YnSection q={q} onPatch={onPatch} /> : null}
      {type === 'DROPDOWN' ? <DdSection q={q} onPatch={onPatch} /> : null}
    </>
  )
}

function QuestionScaleAndMediaTypeSections({
  q,
  onPatch,
  onInstrumentDateFormatChange,
  campaignId,
  instrumentId,
}: Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  onInstrumentDateFormatChange: Props['onInstrumentDateFormatChange']
  campaignId: string
  instrumentId: string
}>) {
  const type = q.config.type
  const isVideo = type === 'VIDEO'
  return (
    <>
      {type === 'RATING' ? <RtSection q={q} onPatch={onPatch} /> : null}
      {type === 'LIKERT' ? <LkSection q={q} onPatch={onPatch} /> : null}
      {type === 'SLIDER' ? <SlSection q={q} onPatch={onPatch} /> : null}
      {type === 'DATE' ? (
        <DaSection q={q} onPatch={onPatch} onInstrumentDateFormatChange={onInstrumentDateFormatChange} />
      ) : null}
      {isVideo ? (
        <VideoQuestionSection
          q={q}
          onPatch={onPatch}
          campaignId={campaignId}
          instrumentId={instrumentId}
        />
      ) : null}
      {isShortTextLikeConfig(q.config) ? <StSection q={q} onPatch={onPatch} /> : null}
      {isVideo ? null : <QuestionLivePreview q={q} onPatch={onPatch} />}
    </>
  )
}

function McCbOptionsSection({
  q,
  onPatch,
}: Readonly<{ q: BuilderQuestion; onPatch: Props['onPatch'] }>) {
  const mcCbConfig =
    q.config.type === 'MULTIPLE_CHOICE' || q.config.type === 'CHECKBOX' ? q.config : null
  const options = mcCbConfig?.data.options ?? []
  const [optRowKeys, setOptRowKeys] = useAnswerOptionRowKeys(q.id, mcCbConfig != null, options)
  const showSaveValidation = useShowBuilderSaveValidation()
  const routingOptionIds = useRoutingOptionIds()

  if (!mcCbConfig) return null

  const configType = mcCbConfig.type
  const d = mcCbConfig.data
  const prefix = configType === 'MULTIPLE_CHOICE' ? 'mc' : 'cb'
  const filled = countFilledAnswerOptions(d.options)
  const minMaxHelper = gateFieldError(
    showSaveValidation,
    errorMinMaxSelection(d.minSelection, d.maxSelection, filled),
  )
  const minFmt = errorInvalidPositiveIntegerField(d.minSelection)
  const maxFmt = errorInvalidPositiveIntegerField(d.maxSelection)
  const rangeErr = minFmt || maxFmt ? null : errorMinMaxOrder(d.minSelection, d.maxSelection)
  const minCap = selectionExceedsAnswerOptionCount(d.minSelection, filled)
  const maxCap = selectionExceedsAnswerOptionCount(d.maxSelection, filled)
  const minSelectionError = gateFieldErrorFlag(
    showSaveValidation,
    Boolean(minFmt || rangeErr || minCap),
  )
  const maxSelectionError = gateFieldErrorFlag(
    showSaveValidation,
    Boolean(maxFmt || rangeErr || maxCap),
  )
  const setData = (patch: Partial<typeof d>) => {
    const merged = { ...mcCbConfig.data, ...patch }
    const next =
      merged.textResponse == null
        ? merged
        : {
            ...merged,
            textResponse: pruneTextResponse(merged.textResponse, {
              optionIds: merged.optionIds ?? [],
              other: merged.other,
              noneOfAbove: merged.noneOfAbove,
            }),
          }
    if (mcCbConfig.type === 'MULTIPLE_CHOICE') {
      onPatch({ config: { type: 'MULTIPLE_CHOICE', data: next } })
      return
    }
    onPatch({ config: { type: 'CHECKBOX', data: next as CheckboxConfig } })
  }
  const textResponse = d.textResponse ?? null
  const specialAddText = (target: 'other' | 'none'): AnswerOptionAddText => {
    const checked = isTextResponseOn(textResponse, target)
    return {
      checked,
      disabled: textResponse != null && !checked,
      onChange: (on) => setData({ textResponse: toggleTextResponse(textResponse, target, on) }),
    }
  }
  const stableOptionIds = d.optionIds ?? optionIdsForLabels(d.options, d.optionIds)

  return (
    <>
      <AnswerOptionsEditorSection
        headerTestId={`answer-options-${prefix}-header`}
        rowKeyPrefix={prefix}
        questionId={q.id}
        options={d.options}
        optionIds={d.optionIds}
        optRowKeys={optRowKeys}
        setOptRowKeys={setOptRowKeys}
        onOptionsChange={(next, nextIds) => setData({ options: next, optionIds: nextIds })}
        textResponse={textResponse}
        onTextResponseToggle={(optionId, on, optionIds) =>
          setData({
            optionIds,
            textResponse: toggleTextResponse(textResponse, 'option', on, optionId),
          })
        }
        exclusiveOptionIds={exclusiveRoutingOptionIdsFor(mcCbConfig, routingOptionIds)}
      />

      {textResponse ? (
        <TextInputSettingsPanel
          value={textResponse}
          optionLabel={textResponseOptionLabel(textResponse, d.options, stableOptionIds)}
          onChange={(nextTr) => setData({ textResponse: nextTr })}
        />
      ) : null}

      <SpecialOptionsGrid
        layoutTestId={`special-options-${prefix}-layout`}
        other={d.other}
        noneOfAbove={d.noneOfAbove}
        onOtherChange={(v) => setData({ other: v })}
        onNoneOfAboveChange={(v) => setData({ noneOfAbove: v })}
        otherAddText={specialAddText('other')}
        noneAddText={specialAddText('none')}
      />

      <Box data-testid="selection-rules-section" sx={{ mt: 3 }}>
        <SelectionRulesMinMaxRow
          minSelection={d.minSelection}
          maxSelection={d.maxSelection}
          onMinChange={(next) => setData({ minSelection: next })}
          onMaxChange={(next) => setData({ maxSelection: next })}
          minError={minSelectionError}
          maxError={maxSelectionError}
        />
      </Box>
      {minMaxHelper ? (
        <FormHelperText error sx={{ mx: 0 }}>
          {minMaxHelper}
        </FormHelperText>
      ) : null}

      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography component="h3" sx={{ ...DISPLAY_SETTINGS_TITLE_TEXT_SX, m: 0, mb: 2 }}>
          Display Settings
        </Typography>
        <Box data-testid={`display-settings-${prefix}-layout-row`} sx={DISPLAY_SETTINGS_TWO_COL_GRID_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <DisplaySettingSwitch
              checked={d.layoutHorizontal}
              onChange={(v) => setData({ layoutHorizontal: v })}
              label="Horizontal layout"
              formControlLabelSx={DISPLAY_SETTINGS_LAYOUT_SWITCH_LABEL_SX}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <DisplaySettingSwitch
              checked={d.multiColumn}
              onChange={(v) => setData({ multiColumn: v })}
              label="Multi-column"
              formControlLabelSx={DISPLAY_SETTINGS_LAYOUT_SWITCH_LABEL_SX}
            />
          </Box>
        </Box>
        <Stack spacing={2}>
          <DisplaySettingSwitch
            checked={d.randomize}
            onChange={(v) => setData({ randomize: v })}
            label="Randomize options"
            formControlLabelSx={DISPLAY_SETTINGS_LAYOUT_SWITCH_LABEL_SX}
          />
          {configType === 'CHECKBOX' ? (
            <DisplaySettingSwitch
              checked={(d as CheckboxConfig).selectAllLabel}
              onChange={(v) => setData({ selectAllLabel: v })}
              label='Show "Select all that apply"'
              formControlLabelSx={DISPLAY_SETTINGS_LAYOUT_SWITCH_LABEL_SX}
            />
          ) : null}
        </Stack>
      </Box>
    </>
  )
}

function YnSection({
  q,
  onPatch,
}: Readonly<{ q: BuilderQuestion; onPatch: Props['onPatch'] }>) {
  const opt1Id = useId()
  const opt2Id = useId()
  if (q.config.type !== 'YES_NO') return null
  const d = q.config.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'YES_NO', data: { ...d, ...patch } } })

  const ynCharCountAdornment = (len: number) => (
    <InputAdornment position="end" sx={{ mr: 0 }}>
      <Typography
        component="span"
        sx={{
          fontSize: '11px',
          lineHeight: '16.5px',
          color: '#a0abbf',
          letterSpacing: '0.0645px',
          whiteSpace: 'nowrap',
        }}
      >
        {len} / {YN_LABEL_MAX}
      </Typography>
    </InputAdornment>
  )

  const ynOptionFieldSx = {
    ...ST_OUTLINED_INPUT_SX,
    '& .MuiOutlinedInput-input': {
      py: '11px',
      px: '12px',
      fontSize: '13px',
      lineHeight: '19.5px',
      letterSpacing: '-0.0762px',
    },
  } as const

  const activePresetIdx = YN_QUICK_PRESETS.findIndex(
    (p) => d.yesLabel.trim() === p.yes && d.noLabel.trim() === p.no,
  )

  return (
    <>
      <Typography component="h3" sx={{ ...DISPLAY_SETTINGS_TITLE_TEXT_SX, mt: 0, mb: 0.5 }}>
        Answer Labels
      </Typography>
      <Typography
        sx={{
          display: 'block',
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          color: '#6b7a90',
          mb: 2,
        }}
      >
        Customize the labels for each option
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: '17px',
          rowGap: 1,
          alignItems: 'flex-start',
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="label" htmlFor={opt1Id} sx={ST_FIELD_LABEL_SX}>
            Option 1
          </Typography>
          <TextField
            id={opt1Id}
            fullWidth
            hiddenLabel
            value={d.yesLabel}
            onChange={(e) => setData({ yesLabel: e.target.value.slice(0, YN_LABEL_MAX) })}
            InputProps={{ endAdornment: ynCharCountAdornment(d.yesLabel.length) }}
            inputProps={{ 'data-testid': 'yn-option-1', maxLength: YN_LABEL_MAX }}
            sx={ynOptionFieldSx}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="label" htmlFor={opt2Id} sx={ST_FIELD_LABEL_SX}>
            Option 2
          </Typography>
          <TextField
            id={opt2Id}
            fullWidth
            hiddenLabel
            value={d.noLabel}
            onChange={(e) => setData({ noLabel: e.target.value.slice(0, YN_LABEL_MAX) })}
            InputProps={{ endAdornment: ynCharCountAdornment(d.noLabel.length) }}
            inputProps={{ 'data-testid': 'yn-option-2', maxLength: YN_LABEL_MAX }}
            sx={ynOptionFieldSx}
          />
        </Box>
      </Box>
      <Typography
        component="p"
        sx={{
          display: 'block',
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: 400,
          color: '#6b7a90',
          mb: 1.5,
          letterSpacing: '-0.1504px',
        }}
      >
        Quick label presets:
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
        {YN_QUICK_PRESETS.map((p, i) => {
          const selected = activePresetIdx === i
          return (
            <Button
              key={p.buttonLabel}
              type="button"
              variant={selected ? 'contained' : 'outlined'}
              disableElevation
              onClick={() => setData({ yesLabel: p.yes, noLabel: p.no })}
              data-testid={`yn-preset-${i}`}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                lineHeight: '19.5px',
                letterSpacing: '-0.0762px',
                minHeight: 34,
                px: 2,
                py: '7.25px',
                borderColor: '#e4e9f0',
                color: selected ? '#fff' : '#0d1b2a',
                bgcolor: selected ? PRIMARY : '#fff',
                '&:hover': {
                  borderColor: selected ? PRIMARY : '#e4e9f0',
                  bgcolor: selected ? PRIMARY : '#f8fafc',
                },
              }}
            >
              {p.buttonLabel}
            </Button>
          )
        })}
      </Stack>
      <Typography sx={DISPLAY_SETTINGS_HEADING_SX}>Display Settings</Typography>
      <Stack spacing={2}>
        <DisplaySettingSwitch
          checked={d.layoutHorizontal}
          onChange={(v) => setData({ layoutHorizontal: v })}
          label="Horizontal layout"
        />
        <DisplaySettingSwitch
          checked={d.toggleStyle}
          onChange={(v) => setData({ toggleStyle: v })}
          label="Use Toggle UI"
        />
      </Stack>
    </>
  )
}

function DdSection({
  q,
  onPatch,
}: Readonly<{ q: BuilderQuestion; onPatch: Props['onPatch'] }>) {
  const dropdownConfig = q.config.type === 'DROPDOWN' ? q.config : null
  const options = dropdownConfig?.data.options ?? []
  const [optRowKeys, setOptRowKeys] = useAnswerOptionRowKeys(q.id, dropdownConfig != null, options)

  if (!dropdownConfig) return null

  const d = dropdownConfig.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'DROPDOWN', data: { ...d, ...patch } } })

  return (
    <>
      <AnswerOptionsEditorSection
        headerTestId="answer-options-dd-header"
        rowKeyPrefix="dd"
        questionId={q.id}
        options={d.options}
        optionIds={d.optionIds}
        optRowKeys={optRowKeys}
        setOptRowKeys={setOptRowKeys}
        onOptionsChange={(next, nextIds) => setData({ options: next, optionIds: nextIds })}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
        <DisplaySettingSwitch checked={d.randomize} onChange={(v) => setData({ randomize: v })} label="Randomize options" />
      </Box>
      <SpecialOptionsGrid
        layoutTestId="special-options-dd-layout"
        other={d.other}
        noneOfAbove={d.noneOfAbove}
        onOtherChange={(v) => setData({ other: v })}
        onNoneOfAboveChange={(v) => setData({ noneOfAbove: v })}
      />
    </>
  )
}

function stInputValidationFormError(
  minFmt: string | null,
  maxFmt: string | null,
  minTooLarge: boolean,
  maxTooLarge: boolean,
  orderErr: string | null,
): string | null {
  if (minFmt) return minFmt
  if (maxFmt) return maxFmt
  if (minTooLarge) return 'Minimum length cannot exceed 2000'
  if (maxTooLarge) return 'Maximum length cannot exceed 2000'
  return orderErr
}

function stHintFieldHelperText(hintTooLong: boolean): string | undefined {
  if (hintTooLong) return 'Placeholder text cannot exceed 500 characters'
  return undefined
}

function StHintFieldLabel({
  type,
  hintId,
}: Readonly<{ type: 'SHORT_TEXT' | 'LONG_TEXT' | 'VIDEO'; hintId: string }>): ReactNode {
  if (type === 'SHORT_TEXT' || type === 'VIDEO') {
    return (
      <Typography component="label" htmlFor={hintId} sx={{ ...ST_FIELD_LABEL_SX, mb: 1 }}>
        Hint text shown inside input
      </Typography>
    )
  }
  return (
    <Typography component="label" htmlFor={hintId} sx={{ ...ST_FIELD_LABEL_SX, mb: 1 }}>
      Placeholder Text
    </Typography>
  )
}

function StSection({
  q,
  onPatch,
}: Readonly<{ q: BuilderQuestion; onPatch: Props['onPatch'] }>) {
  const baseId = useId()
  const showSaveValidation = useShowBuilderSaveValidation()
  if (!isShortTextLikeConfig(q.config)) return null
  const { type, data: d } = q.config
  const minId = `${baseId}-st-min`
  const maxId = `${baseId}-st-max`
  const hintId = `${baseId}-st-hint`

  const minFmt = errorInvalidPositiveIntegerField(d.minLength)
  const maxFmt = errorInvalidPositiveIntegerField(d.maxLength)
  const minT = d.minLength.trim()
  const maxT = d.maxLength.trim()
  const minTooLarge = Boolean(!minFmt && minT && Number(minT) > 2000)
  const maxTooLarge = Boolean(!maxFmt && maxT && Number(maxT) > 2000)
  const orderErr = errorShortTextLengthOrder(d.minLength, d.maxLength)
  const hintTooLong = d.hintPlaceholder.length > 500

  const setData = (patch: Partial<ShortTextConfig>) => {
    if (type === 'VIDEO') {
      onPatch({ config: { type: 'VIDEO', data: { ...d, ...patch } } })
      return
    }
    onPatch({ config: { type, data: { ...d, ...patch } } })
  }

  const minErr = gateFieldErrorFlag(showSaveValidation, Boolean(minFmt || minTooLarge))
  const maxErr = gateFieldErrorFlag(showSaveValidation, Boolean(maxFmt || maxTooLarge || orderErr))
  const validationMsg = gateFieldError(
    showSaveValidation,
    stInputValidationFormError(minFmt, maxFmt, minTooLarge, maxTooLarge, orderErr),
  )
  const hintHelperText = showSaveValidation ? stHintFieldHelperText(hintTooLong) : undefined
  const gridMb = orderErr ? 0.5 : 0
  const useShortPlaceholders = type === 'SHORT_TEXT' || type === 'VIDEO'
  const minPlaceholder =
    useShortPlaceholders ? SHORT_TEXT_MIN_LENGTH_PLACEHOLDER : 'e.g. 50'
  const maxPlaceholder =
    useShortPlaceholders ? SHORT_TEXT_MAX_LENGTH_PLACEHOLDER : 'e.g. 500'
  const hintPlaceholderField =
    useShortPlaceholders ? ST_SHORT_TEXT_HINT_FIELD_PLACEHOLDER : ST_LONG_TEXT_HINT_FIELD_PLACEHOLDER
  const hintOutlinedSx =
    useShortPlaceholders ? ST_SHORT_TEXT_HINT_FIELD_SX : ST_LONG_TEXT_HINT_FIELD_SX

  return (
    <>
      <Typography
        component="h3"
        sx={{ ...ST_INPUT_VALIDATION_HEADING_SX, ...(type === 'VIDEO' ? { mt: 3 } : null) }}
      >
        Input Validation
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: '17px',
          rowGap: 1,
          alignItems: 'flex-start',
          mb: gridMb,
        }}
      >
        <Box
          data-testid="st-input-validation-min-column"
          sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <Typography
            component="label"
            htmlFor={minId}
            sx={{
              ...ST_FIELD_LABEL_SX,
              mb: 0,
              minHeight: '21px',
              lineHeight: '21px',
              whiteSpace: 'nowrap',
            }}
          >
            Minimum characters
          </Typography>
          <TextField
            id={minId}
            fullWidth
            hiddenLabel
            placeholder={minPlaceholder}
            value={d.minLength}
            onChange={(e) => setData({ minLength: sanitizePositiveIntegerDigits(e.target.value, 4) })}
            error={minErr}
            inputProps={{ inputMode: 'numeric', 'data-testid': 'short-text-min-length' }}
            sx={ST_OUTLINED_INPUT_SX}
          />
        </Box>
        <Box
          data-testid="st-input-validation-max-column"
          sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <Typography
            component="label"
            htmlFor={maxId}
            sx={{
              ...ST_FIELD_LABEL_SX,
              mb: 0,
              minHeight: '21px',
              lineHeight: '21px',
              whiteSpace: 'nowrap',
            }}
          >
            Maximum characters
          </Typography>
          <TextField
            id={maxId}
            fullWidth
            hiddenLabel
            placeholder={maxPlaceholder}
            value={d.maxLength}
            onChange={(e) => setData({ maxLength: sanitizePositiveIntegerDigits(e.target.value, 4) })}
            error={maxErr}
            inputProps={{ inputMode: 'numeric', 'data-testid': 'short-text-max-length' }}
            sx={ST_OUTLINED_INPUT_SX}
          />
        </Box>
      </Box>
      {validationMsg ? (
        <FormHelperText error sx={{ mx: 0, mb: 0 }}>
          {validationMsg}
        </FormHelperText>
      ) : null}

      <Typography sx={DISPLAY_SETTINGS_HEADING_SX}>Display Settings</Typography>
      <StHintFieldLabel type={type} hintId={hintId} />
      <TextField
        id={hintId}
        fullWidth
        hiddenLabel
        placeholder={hintPlaceholderField}
        value={d.hintPlaceholder}
        onChange={(e) => setData({ hintPlaceholder: e.target.value.slice(0, 500) })}
        error={showSaveValidation && hintTooLong}
        helperText={hintHelperText}
        inputProps={{ 'data-testid': 'short-text-hint' }}
        sx={hintOutlinedSx}
      />
    </>
  )
}

function DaSection({
  q,
  onPatch,
  onInstrumentDateFormatChange,
}: Readonly<{
  q: BuilderQuestion
  onPatch: Props['onPatch']
  onInstrumentDateFormatChange: Props['onInstrumentDateFormatChange']
}>) {
  const baseId = useId()
  const earliestId = `${baseId}-date-earliest`
  const latestId = `${baseId}-date-latest`
  const hintId = `${baseId}-date-hint`
  const showSaveValidation = useShowBuilderSaveValidation()

  if (q.config.type !== 'DATE') return null
  const d = q.config.data
  const setData = (patch: Partial<typeof d>) =>
    onPatch({ config: { type: 'DATE', data: { ...d, ...patch } } })

  const earliestFmt = errorOptionalIsoDateField(d.earliestSelectableDate)
  const latestFmt = errorOptionalIsoDateField(d.latestSelectableDate)
  const rangeErr = errorDateRangeOrder(d.earliestSelectableDate, d.latestSelectableDate)
  const hintTooLong = d.hintPlaceholder.length > DATE_HINT_PLACEHOLDER_MAX
  const earliestErr = gateFieldErrorFlag(showSaveValidation, Boolean(earliestFmt || rangeErr))
  const latestErr = gateFieldErrorFlag(showSaveValidation, Boolean(latestFmt || rangeErr))
  const dateRestrictionMsg = gateFieldError(
    showSaveValidation,
    earliestFmt ?? latestFmt ?? rangeErr,
  )
  const hintFieldError = showSaveValidation && hintTooLong

  const eTrim = d.earliestSelectableDate.trim()
  const lTrim = d.latestSelectableDate.trim()
  const earliestVal = eTrim && !earliestFmt ? eTrim : ''
  const latestVal = lTrim && !latestFmt ? lTrim : ''
  const earliestDay = dayFromStorageIso(earliestVal)
  const latestDay = dayFromStorageIso(latestVal)
  const pickerFmt = datePickerFormatToken(d.displayFormat)

  const dateHintCountAdornment = (
    <InputAdornment position="end" sx={{ ml: 0.5, maxHeight: 'none', alignSelf: 'center' }}>
      <Typography
        component="span"
        data-testid="date-placeholder-count"
        sx={{
          fontSize: '11px',
          lineHeight: '16.5px',
          letterSpacing: '0.0645px',
          color: '#a0abbf',
          whiteSpace: 'nowrap',
        }}
        aria-live="polite"
      >
        {d.hintPlaceholder.length} / {DATE_HINT_PLACEHOLDER_MAX}
      </Typography>
    </InputAdornment>
  )

  const applyDisplayFormat = (next: DateInputDisplayFormat) => {
    if (onInstrumentDateFormatChange) {
      onInstrumentDateFormatChange(next)
    } else {
      setData({ displayFormat: next })
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Typography component="h3" sx={{ ...ST_INPUT_VALIDATION_HEADING_SX, mt: 2 }}>
        Date Restrictions
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 2,
          rowGap: 1,
          alignItems: 'flex-start',
          mb: earliestFmt || latestFmt || rangeErr ? 0.5 : 1,
        }}
        data-testid="date-restrictions-row"
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography component="label" htmlFor={earliestId} sx={{ ...ST_FIELD_LABEL_SX, mb: 0 }}>
            Earliest selectable date
          </Typography>
          <DatePicker
            format={pickerFmt}
            value={earliestDay}
            onChange={(v) => setData({ earliestSelectableDate: storageIsoFromDay(v) })}
            maxDate={latestDay ?? undefined}
            desktopModeMediaQuery="@media (min-width: 0px)"
            slots={INSTRUMENT_DATE_PICKER_SLOTS}
            slotProps={{
              ...INSTRUMENT_DATE_CALENDAR_SLOT_PROPS,
              textField: {
                id: earliestId,
                fullWidth: true,
                hiddenLabel: true,
                error: earliestErr,
                inputProps: { 'data-testid': 'date-earliest-input' },
                sx: INSTRUMENT_DATE_PICKER_FIELD_SX,
              },
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography component="label" htmlFor={latestId} sx={{ ...ST_FIELD_LABEL_SX, mb: 0 }}>
            Latest selectable date
          </Typography>
          <DatePicker
            format={pickerFmt}
            value={latestDay}
            onChange={(v) => setData({ latestSelectableDate: storageIsoFromDay(v) })}
            minDate={earliestDay ?? undefined}
            desktopModeMediaQuery="@media (min-width: 0px)"
            slots={INSTRUMENT_DATE_PICKER_SLOTS}
            slotProps={{
              ...INSTRUMENT_DATE_CALENDAR_SLOT_PROPS,
              textField: {
                id: latestId,
                fullWidth: true,
                hiddenLabel: true,
                error: latestErr,
                inputProps: { 'data-testid': 'date-latest-input' },
                sx: INSTRUMENT_DATE_PICKER_FIELD_SX,
              },
            }}
          />
        </Box>
      </Box>
      {dateRestrictionMsg ? (
        <FormHelperText error sx={{ mx: 0, mb: 1 }}>
          {dateRestrictionMsg}
        </FormHelperText>
      ) : null}

      <Typography component="h3" sx={{ ...ST_INPUT_VALIDATION_HEADING_SX, mt: 2, mb: 1.5 }}>
        Restrictions Toggles
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <DisplaySettingSwitch
          checked={d.disablePastDates}
          onChange={(v) => setData({ disablePastDates: v })}
          label="Disable past dates"
        />
        <DisplaySettingSwitch
          checked={d.disableFutureDates}
          onChange={(v) => setData({ disableFutureDates: v })}
          label="Disable future dates"
        />
      </Stack>
      {d.disablePastDates && d.disableFutureDates ? (
        <Typography
          data-testid="date-today-only-warning"
          sx={{ fontSize: '12px', lineHeight: '18px', color: '#a0abbf', mb: 2 }}
        >
          Only today&apos;s date will be selectable for respondents
        </Typography>
      ) : null}

      <Typography component="h3" sx={{ ...ST_INPUT_VALIDATION_HEADING_SX, mt: 1, mb: 1 }}>
        Format Settings
      </Typography>
      <Typography sx={{ fontSize: '14px', lineHeight: '21px', fontWeight: 500, color: '#6b7a90', letterSpacing: '-0.1504px', mb: 1.5 }}>
        Date Format
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={d.displayFormat}
        onChange={(_, v) => {
          if (v == null) return
          applyDisplayFormat(v)
        }}
        aria-label="Date format"
        data-testid="date-format-toggle"
        sx={{
          display: 'inline-flex',
          alignSelf: 'flex-start',
          gap: `${DATE_FORMAT_CHIP_GAP_PX}px`,
          mb: 0,
          width: 'fit-content',
          '& .MuiToggleButtonGroup-grouped': {
            m: 0,
            flex: 'none',
            border: `1px solid ${DATE_FORMAT_CHIP_BORDER_COLOR} !important`,
            borderRadius: '10px !important',
          },
        }}
      >
        <ToggleButton
          value="dd/MM/yyyy"
          sx={{ ...DATE_FORMAT_CHIP_SX, width: DATE_FORMAT_CHIP_DD_MM_WIDTH_PX, minWidth: DATE_FORMAT_CHIP_DD_MM_WIDTH_PX }}
        >
          DD/MM/YYYY
        </ToggleButton>
        <ToggleButton
          value="MM/dd/yyyy"
          sx={{ ...DATE_FORMAT_CHIP_SX, width: DATE_FORMAT_CHIP_MM_DD_WIDTH_PX, minWidth: DATE_FORMAT_CHIP_MM_DD_WIDTH_PX }}
        >
          MM/DD/YYYY
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#a0abbf', mt: 1, mb: 2 }}>
        Applies to all date questions in this instrument
      </Typography>

      <Typography component="label" htmlFor={hintId} sx={{ ...ST_FIELD_LABEL_SX, mb: 1 }}>
        Placeholder Text
      </Typography>
      <TextField
        id={hintId}
        fullWidth
        hiddenLabel
        placeholder="e.g. Select your date of birth"
        value={d.hintPlaceholder}
        onChange={(e) =>
          setData({ hintPlaceholder: e.target.value.slice(0, DATE_HINT_PLACEHOLDER_MAX) })
        }
        error={hintFieldError}
        helperText={
          hintFieldError
            ? `Placeholder text cannot exceed ${DATE_HINT_PLACEHOLDER_MAX} characters`
            : undefined
        }
        inputProps={{
          maxLength: DATE_HINT_PLACEHOLDER_MAX,
          'data-testid': 'date-placeholder-input',
        }}
        InputProps={{ endAdornment: dateHintCountAdornment }}
        sx={{
          ...ST_DATE_PLACEHOLDER_FIELD_SX,
          '& .MuiInputBase-adornedEnd': { pr: '8px' },
        }}
      />
    </LocalizationProvider>
  )
}

/** Next single `*` that is not part of `**`, starting at `from` (inclusive). */
function findClosingSingleItalicStar(text: string, from: number): number {
  for (let j = from; j < text.length; j++) {
    if (text[j] === '*' && j + 1 < text.length && text[j + 1] === '*') {
      j++
      continue
    }
    if (text[j] === '*') return j
  }
  return -1
}

type MarkdownPreviewScanCtx = {
  flush: () => void
  nodes: ReactNode[]
  key: { n: number }
}

function tryConsumeTripleEmPreview(text: string, i: number, ctx: MarkdownPreviewScanCtx): number | null {
  if (!(text[i] === '*' && i + 2 < text.length && text[i + 1] === '*' && text[i + 2] === '*')) return null
  const innerStart = i + 3
  let j = innerStart
  while (j < text.length && text[j] !== '*') j++
  if (!(j + 2 < text.length && text[j] === '*' && text[j + 1] === '*' && text[j + 2] === '*')) return null
  const inner = text.slice(innerStart, j)
  if (inner.includes('*')) return null
  ctx.flush()
  const kn = ctx.key.n++
  ctx.nodes.push(
    <Box component="span" key={`mbi-${kn}`} sx={{ fontWeight: 700, fontStyle: 'italic' }}>
      {inner}
    </Box>,
  )
  return j + 3
}

/** `**…**` bold span, or literal `**` pair when unclosed. */
function tryConsumeBoldEmPreview(
  text: string,
  i: number,
  ctx: MarkdownPreviewScanCtx,
): number | 'literal_pair' | null {
  if (!(text[i] === '*' && i + 1 < text.length && text[i + 1] === '*')) return null
  const close = text.indexOf('**', i + 2)
  if (close === -1) return 'literal_pair'
  ctx.flush()
  const inner = text.slice(i + 2, close)
  const kn = ctx.key.n++
  ctx.nodes.push(
    <Box component="span" key={`mb-${kn}`} sx={{ fontWeight: 700 }}>
      {inner}
    </Box>,
  )
  return close + 2
}

function tryConsumeSingleItalicPreview(text: string, i: number, ctx: MarkdownPreviewScanCtx): number | null {
  if (!(text[i] === '*' && (i + 1 >= text.length || text[i + 1] !== '*'))) return null
  const close = findClosingSingleItalicStar(text, i + 1)
  if (close === -1) return null
  const inner = text.slice(i + 1, close)
  if (inner.includes('*')) return null
  ctx.flush()
  const kn = ctx.key.n++
  ctx.nodes.push(
    <Box component="span" key={`mi-${kn}`} sx={{ fontStyle: 'italic' }}>
      {inner}
    </Box>,
  )
  return close + 1
}

/** Live preview: `***…***` → bold+italic; `**…**` → bold; `*…*` (single stars) → italic. */
function renderInlineMarkdownPreview(text: string): ReactNode {
  const nodes: ReactNode[] = []
  let buf = ''
  let i = 0
  const key = { n: 0 }
  const flush = () => {
    if (buf) {
      nodes.push(buf)
      buf = ''
    }
  }
  const ctx: MarkdownPreviewScanCtx = { flush, nodes, key }
  while (i < text.length) {
    const tripleNext = tryConsumeTripleEmPreview(text, i, ctx)
    if (tripleNext != null) {
      i = tripleNext
      continue
    }
    const boldNext = tryConsumeBoldEmPreview(text, i, ctx)
    if (boldNext === 'literal_pair') {
      buf += text[i]
      buf += text[i + 1]
      i += 2
      continue
    }
    if (boldNext != null) {
      i = boldNext
      continue
    }
    const italicNext = tryConsumeSingleItalicPreview(text, i, ctx)
    if (italicNext != null) {
      i = italicNext
      continue
    }
    buf += text[i]
    i++
  }
  flush()
  if (nodes.length === 0) return text
  if (nodes.length === 1) {
    const only = nodes[0]
    return only === undefined ? text : only
  }
  return <>{nodes}</>
}

export function instrumentPreviewQuestionPrompt(q: BuilderQuestion): ReactNode {
  const plainQ = q.questionText.trim()
  const fmtQ = q.questionTextFormat ?? defaultBuilderTextFormatFlags
  const questionPreviewSource =
    fmtQ.bold || fmtQ.italic ? composeWholeFieldMarkdown(plainQ, fmtQ) : plainQ
  return questionPreviewSource
    ? renderInlineMarkdownPreview(questionPreviewSource)
    : 'Your question will appear here'
}

export function instrumentPreviewQuestionDescription(q: BuilderQuestion): ReactNode | null {
  const plainD = q.description.trim()
  if (!plainD) return null
  const fmtD = q.descriptionFormat ?? defaultBuilderTextFormatFlags
  const descPreviewSource =
    fmtD.bold || fmtD.italic ? composeWholeFieldMarkdown(plainD, fmtD) : plainD
  return renderInlineMarkdownPreview(descPreviewSource)
}

function SortableRankingPreviewRow({
  id,
  rank,
  row,
  disabled,
}: Readonly<{
  id: string
  rank: number
  row: PictureChoiceOptionRow
  disabled: boolean
}>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  let rowSurfaceOpacity = 1
  if (disabled) rowSurfaceOpacity = 0.55
  else if (isDragging) rowSurfaceOpacity = 0.92
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: rowSurfaceOpacity,
  } as const
  const hasImg = row.imageUrl.trim().length > 0
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        ...RANKING_ROW_SURFACE_SX,
        opacity: rowSurfaceOpacity,
      }}
    >
      <IconButton
        type="button"
        size="small"
        disabled={disabled}
        {...attributes}
        {...listeners}
        aria-label={`Reorder rank ${rank}`}
        sx={{ color: '#cbd5e1', p: 0.5, flexShrink: 0 }}
      >
        <DragIndicatorIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: PRIMARY,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {rank}
      </Box>
      {row.caption.trim() ? (
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '13px',
            color: '#0f172a',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.caption.trim()}
        </Typography>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />
      )}
      {hasImg ? (
        <Box
          component="img"
          src={row.imageUrl}
          alt=""
          sx={{
            width: 38,
            height: 38,
            borderRadius: '5px',
            objectFit: 'cover',
            bgcolor: '#f1f5f9',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box sx={{ width: 38, height: 38, borderRadius: '5px', bgcolor: '#f1f5f9', flexShrink: 0 }} aria-hidden />
      )}
    </Box>
  )
}

function RankingLivePreviewBody({
  data,
  optsErr,
  resetSig,
}: Readonly<{
  data: RankingConfig
  optsErr: string | null
  resetSig: string
}>) {
  const [order, setOrder] = useState(() => data.options.map((o) => o.id))
  useEffect(() => {
    setOrder(data.options.map((o) => o.id))
  }, [resetSig])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const onDragEnd = (event: DragEndEvent) => {
    if (optsErr) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const activeId = String(active.id)
    const overId = String(over.id)
    const oldIndex = order.indexOf(activeId)
    const newIndex = order.indexOf(overId)
    if (oldIndex < 0 || newIndex < 0) return
    setOrder((prev) => arrayMove(prev, oldIndex, newIndex))
  }

  const disabled = Boolean(optsErr)

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        data-testid="ranking-live-preview-card"
        sx={{
          bgcolor: PREVIEW_CARD_BG,
          border: `1px solid ${PREVIEW_CARD_BORDER}`,
          borderRadius: '10px',
          pt: '21px',
          pb: 2,
          px: '25px',
        }}
      >
        <Typography sx={{ fontSize: '13px', lineHeight: '19.5px', color: '#6b7a90', mb: 2 }}>
          Drag to rank from most to least preferred
        </Typography>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <Stack spacing={1}>
              {order.map((id, i) => {
                const row = data.options.find((o) => o.id === id)
                if (!row) return null
                return (
                  <SortableRankingPreviewRow key={id} id={id} rank={i + 1} row={row} disabled={disabled} />
                )
              })}
            </Stack>
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  )
}

function RatingLivePreviewBlock({
  data,
  resetSig,
}: Readonly<{
  data: RatingConfig
  resetSig: string
}>) {
  const [previewValue, setPreviewValue] = useState(1)
  useEffect(() => {
    setPreviewValue(1)
  }, [resetSig])

  const n = data.scaleMax
  const isStars = data.displayStyle === 'stars'
  const low = data.lowLabel.trim()
  const high = data.highLabel.trim()

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          bgcolor: PREVIEW_CARD_BG,
          border: `1px solid ${PREVIEW_CARD_BORDER}`,
          borderRadius: '10px',
          pt: '21px',
          pb: 2,
          px: '25px',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            maxWidth: '100%',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            data-testid="rating-preview-star-row"
            sx={{ flexWrap: 'wrap', alignItems: 'center', width: 'fit-content', gap: '8px' }}
          >
            {isStars
              ? Array.from({ length: n }, (_, i) => {
                  const v = i + 1
                  const filled = v <= previewValue
                  return (
                    <Box
                      key={v}
                      component="button"
                      type="button"
                      aria-pressed={filled}
                      aria-label={`Rate ${v} of ${n}`}
                      data-testid={`preview-rating-star-${v}`}
                      onClick={() => setPreviewValue(v)}
                      sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', lineHeight: 0 }}
                    >
                      <RatingPreviewStarIcon filled={filled} value={v} />
                    </Box>
                  )
                })
              : Array.from({ length: n }, (_, i) => {
                  const v = i + 1
                  const selected = v === previewValue
                  return (
                    <Box
                      key={v}
                      component="button"
                      type="button"
                      aria-pressed={selected}
                      aria-label={`Select ${v}`}
                      data-testid={`preview-rating-num-${v}`}
                      onClick={() => setPreviewValue(v)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        border: selected ? `2px solid ${PRIMARY}` : '1px solid #e4e9f0',
                        bgcolor: selected ? PRIMARY : '#fff',
                        color: selected ? '#fff' : '#0f172a',
                        fontWeight: 600,
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {v}
                    </Box>
                  )
                })}
          </Stack>
          {low || high ? (
            <Stack
              direction="row"
              data-testid="rating-preview-label-row"
              justifyContent={ratingPreviewLabelRowJustify(low, high)}
              sx={{ mt: 1.5, width: '100%', columnGap: 1 }}
            >
              {low ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#6b7a90',
                    textAlign: 'left',
                    maxWidth: ratingPreviewLabelMaxWidth(low, high),
                    wordBreak: 'break-word',
                  }}
                >
                  {low}
                </Typography>
              ) : null}
              {high ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#6b7a90',
                    textAlign: 'right',
                    maxWidth: ratingPreviewLabelMaxWidth(low, high),
                    wordBreak: 'break-word',
                  }}
                >
                  {high}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}

/** Likert horizontal strip (Figma 4399:9653–9674): column width LIKERT_H_COLUMN_WIDTH_PX, 32px between columns, 8px radio→label. */
const LIKERT_H_STRIP_GAP_PX = 32
/** Horizontal Likert preview column width (px); matches Figma `4399:9655` frame (`w-[80px]`). */
const LIKERT_H_COLUMN_WIDTH_PX = 80
const LIKERT_H_RADIO_LABEL_GAP_PX = 8

function likertRespondentLabel(labels: readonly string[], i: number): string {
  const t = (labels[i] ?? '').trim()
  return t.length > 0 ? t : String(i + 1)
}

function LikertLivePreviewBlock({
  data,
  resetSig,
}: Readonly<{
  data: LikertConfig
  resetSig: string
}>) {
  const [previewIdx, setPreviewIdx] = useState(0)
  useEffect(() => {
    setPreviewIdx(0)
  }, [resetSig])

  const n = data.pointCount
  const labels = data.labels
  const likertPointKeys = useMemo(() => Array.from({ length: n }, () => crypto.randomUUID()), [resetSig, n])

  /** Figma 4399:9656 — unselected: white fill + 1.5px #cbd5e1 ring only (no inner accent ring). Selected: primary ring + centered dot. */
  const renderRadioOnly = (_i: number, selected: boolean) =>
    selected ? (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: `2px solid ${PRIMARY}`,
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PRIMARY }} />
      </Box>
    ) : (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: '1.5px solid #cbd5e1',
          bgcolor: '#ffffff',
          flexShrink: 0,
        }}
      />
    )

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        data-testid="likert-live-preview-card"
        sx={{
          bgcolor: PREVIEW_CARD_BG,
          border: `1px solid ${PREVIEW_CARD_BORDER}`,
          borderRadius: '10px',
          pt: '25px',
          pb: 2,
          px: '33px',
        }}
      >
        {data.layoutHorizontal ? (
          <Box
            data-testid="likert-horizontal-scale-row"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'flex-start',
              columnGap: `${LIKERT_H_STRIP_GAP_PX}px`,
              rowGap: `${LIKERT_H_STRIP_GAP_PX}px`,
              width: '100%',
            }}
          >
            {Array.from({ length: n }, (_, i) => {
              const selected = i === previewIdx
              const text = likertRespondentLabel(labels, i)
              return (
                <Box
                  key={likertPointKeys[i]}
                  sx={{
                    width: LIKERT_H_COLUMN_WIDTH_PX,
                    minWidth: LIKERT_H_COLUMN_WIDTH_PX,
                    maxWidth: LIKERT_H_COLUMN_WIDTH_PX,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: `${LIKERT_H_RADIO_LABEL_GAP_PX}px`,
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-pressed={selected}
                    aria-label={`Likert scale option ${i + 1}`}
                    data-testid={`preview-likert-${i}`}
                    onClick={() => setPreviewIdx(i)}
                    sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', lineHeight: 0 }}
                  >
                    {renderRadioOnly(i, selected)}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '15px',
                      color: '#6b7a90',
                      textAlign: 'center',
                      width: '100%',
                      minHeight: 30,
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        ) : (
          <Stack spacing={2}>
            {Array.from({ length: n }, (_, i) => {
              const selected = i === previewIdx
              const text = likertRespondentLabel(labels, i)
              return (
                <Stack direction="row" key={likertPointKeys[i]} spacing={1.25} alignItems="center">
                  <Box
                    component="button"
                    type="button"
                    aria-pressed={selected}
                    aria-label={`Likert scale option ${i + 1}`}
                    data-testid={`preview-likert-${i}`}
                    onClick={() => setPreviewIdx(i)}
                    sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', lineHeight: 0 }}
                  >
                    {renderRadioOnly(i, selected)}
                  </Box>
                  <Typography sx={{ fontSize: '12px', lineHeight: '16px', color: '#6b7a90', textAlign: 'left' }}>
                    {text}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>
    </Box>
  )
}

function SliderLivePreviewBlock({
  data,
  resetSig,
  rangeError,
  onPatch,
}: Readonly<{
  data: SliderConfig
  resetSig: string
  rangeError: string | null
  onPatch?: (patch: QuestionBuilderSurfacePatch) => void
}>) {
  const bounds = useMemo(() => {
    if (rangeError != null) return null
    const min = effectiveSliderMinValue(data.minValue)
    const max = effectiveSliderMaxValue(data.maxValue)
    const step = Number(data.step.trim())
    if (!Number.isSafeInteger(step)) return null
    if (step < 1 || min >= max || (max - min) % step !== 0) return null
    return { min, max, step }
  }, [rangeError, data.minValue, data.maxValue, data.step])

  const [val, setVal] = useState(0)

  useEffect(() => {
    if (!bounds) {
      setVal(0)
      return
    }
    setVal(alignSliderPreviewValue((bounds.min + bounds.max) / 2, bounds.min, bounds.max, bounds.step))
  }, [resetSig, bounds])

  const low = data.lowLabel.trim()
  const high = data.highLabel.trim()
  const leftEnd = low || (bounds ? String(bounds.min) : '')
  const rightEnd = high || (bounds ? String(bounds.max) : '')

  const applyPreset = (min: string, max: string, step: string) => {
    if (!onPatch) return
    onPatch({
      config: { type: 'SLIDER', data: { ...data, minValue: min, maxValue: max, step } },
    })
  }

  if (!bounds) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: '14px', color: SECONDARY }}>
          {rangeError ?? 'Adjust range settings to preview.'}
        </Typography>
      </Box>
    )
  }

  const preset15 = bounds.min === 1 && bounds.max === 5 && bounds.step === 1
  const preset110 = bounds.min === 1 && bounds.max === 10 && bounds.step === 1

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          bgcolor: PREVIEW_CARD_BG,
          border: `1px solid ${PREVIEW_CARD_BORDER}`,
          borderRadius: '10px',
          pt: '21px',
          pb: 2,
          px: '25px',
        }}
      >
        <Box sx={{ px: 0.5 }}>
          <Slider
            data-testid="preview-slider"
            value={val}
            onChange={(_, v) => setVal(v as number)}
            min={bounds.min}
            max={bounds.max}
            step={bounds.step}
            valueLabelDisplay="on"
            aria-label="Preview numeric slider"
            sx={{
              mt: 1,
              height: 6,
              '& .MuiSlider-rail': {
                height: 6,
                opacity: 1,
                bgcolor: '#e4e9f0',
                borderRadius: '3px',
              },
              '& .MuiSlider-track': {
                height: 6,
                border: 'none',
                bgcolor: PRIMARY,
                borderRadius: '3px',
              },
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
                bgcolor: '#fff',
                border: `2px solid ${PRIMARY}`,
                boxShadow: '0px 2px 6px rgba(15, 23, 42, 0.12)',
                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                  boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.18)',
                },
              },
              '& .MuiSlider-valueLabel': {
                fontSize: '14px',
                fontWeight: 600,
                color: '#0f172a',
                bgcolor: 'transparent',
                '&::before': { display: 'none' },
              },
            }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            data-testid="slider-preview-end-labels"
            sx={{ mt: 1, px: 0.25 }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: '#6b7a90',
                textAlign: 'left',
                maxWidth: '48%',
                wordBreak: 'break-word',
              }}
            >
              {leftEnd}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: '#6b7a90',
                textAlign: 'right',
                maxWidth: '48%',
                wordBreak: 'break-word',
              }}
            >
              {rightEnd}
            </Typography>
          </Stack>
        </Box>
        {onPatch ? (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={preset15 ? 'contained' : 'outlined'}
              data-testid="slider-preset-1-5"
              onClick={() => applyPreset('1', '5', '1')}
              sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
            >
              1 – 5
            </Button>
            <Button
              size="small"
              variant={preset110 ? 'contained' : 'outlined'}
              data-testid="slider-preset-1-10"
              onClick={() => applyPreset('1', '10', '1')}
              sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
            >
              1 – 10
            </Button>
          </Stack>
        ) : null}
      </Box>
    </Box>
  )
}

function fingerprintPictureOptionsForReset(
  rows: readonly { id: string; imageUrl: string; caption: string }[],
): string {
  return rows.map((o) => `${o.id}:${String(o.imageUrl.length)}:${o.caption}`).join('\u0001')
}

function ratingPreviewLabelRowJustify(
  low: string,
  high: string,
): 'space-between' | 'flex-end' | 'flex-start' {
  if (low.length > 0 && high.length > 0) return 'space-between'
  if (high.length > 0) return 'flex-end'
  return 'flex-start'
}

function ratingPreviewLabelMaxWidth(low: string, high: string): string {
  if (low.length > 0 && high.length > 0) return '48%'
  return '100%'
}

function mcCbSelectionHintText(minSel: number, maxSel: number, maxB: number | undefined): string | null {
  if (minSel > 0 && Number.isFinite(maxSel)) {
    return `Select between ${minSel} and ${maxB} answer(s).`
  }
  if (minSel > 0) {
    return `Select at least ${minSel} answer(s).`
  }
  if (maxB !== undefined) {
    return `Select up to ${maxB} answer(s).`
  }
  return null
}

function mcCbPreviewBodyLayout(
  rowsBase: readonly { index: number; label: string }[],
  extra: readonly { key: PreviewSelKey; label: string }[],
  renderRowBtn: (key: PreviewSelKey, label: string, testId: string) => ReactNode,
  useTwoColGrid: boolean,
  layoutHorizontal: boolean,
): ReactNode {
  if (rowsBase.length === 0 && extra.length === 0) {
    return (
      <Typography sx={{ fontSize: '14px', color: SECONDARY }}>Add answer options to see them in the preview.</Typography>
    )
  }
  if (useTwoColGrid) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: '32px',
          width: '100%',
        }}
      >
        {rowsBase.map((r) => renderRowBtn(optKey(r.index), r.label, `preview-opt-${r.index}`))}
        {extra.map((e) => renderRowBtn(e.key, e.label, `preview-${e.key}`))}
      </Box>
    )
  }
  return (
    <Stack
      direction={layoutHorizontal ? 'row' : 'column'}
      useFlexGap
      sx={{
        flexWrap: layoutHorizontal ? 'wrap' : 'nowrap',
        gap: '32px',
      }}
    >
      {rowsBase.map((r) => renderRowBtn(optKey(r.index), r.label, `preview-opt-${r.index}`))}
      {extra.map((e) => renderRowBtn(e.key, e.label, `preview-${e.key}`))}
    </Stack>
  )
}

function mcCbExclusivePreviewKeys(
  d: MultipleChoiceConfig | CheckboxConfig,
  config: BuilderQuestion['config'],
  routingOptionIds: ReadonlySet<string>,
): Set<PreviewSelKey> {
  const exclusiveOptionIds = exclusiveRoutingOptionIdsFor(config, routingOptionIds)
  const exclusiveKeys = new Set<PreviewSelKey>()
  for (const [index, optionId] of (d.optionIds ?? []).entries()) {
    if (exclusiveOptionIds.has(optionId)) exclusiveKeys.add(optKey(index))
  }
  return exclusiveKeys
}

function mcCbPreviewMinMaxError(
  d: MultipleChoiceConfig | CheckboxConfig,
  filledCount: number,
): string | null {
  return (
    errorInvalidPositiveIntegerField(d.minSelection) ||
    errorInvalidPositiveIntegerField(d.maxSelection) ||
    errorMinMaxOrder(d.minSelection, d.maxSelection) ||
    errorMinMaxSelection(d.minSelection, d.maxSelection, filledCount)
  )
}

function mcCbPreviewExtraOptions(d: MultipleChoiceConfig | CheckboxConfig): { key: PreviewSelKey; label: string }[] {
  const extra: { key: PreviewSelKey; label: string }[] = []
  if (d.other) extra.push({ key: 'other', label: PREVIEW_OTHER_LABEL })
  if (d.noneOfAbove) extra.push({ key: 'none', label: PREVIEW_NONE_LABEL })
  return extra
}

function McCbLivePreviewBlock({
  q,
  selectedKeys,
  setSelectedKeys,
}: Readonly<{
  q: BuilderQuestion
  selectedKeys: ReadonlySet<PreviewSelKey>
  setSelectedKeys: Dispatch<SetStateAction<ReadonlySet<PreviewSelKey>>>
}>): ReactNode {
  const routingOptionIds = useRoutingOptionIds()
  if (!(q.config.type === 'MULTIPLE_CHOICE' || q.config.type === 'CHECKBOX')) {
    return null
  }
  const d = q.config.data
  const filled = filledOptionRows(d.options)
  const exclusiveKeys = mcCbExclusivePreviewKeys(d, q.config, routingOptionIds)
  const filledCount = countFilledAnswerOptions(d.options)
  const minB = parsePositiveIntStrict(d.minSelection)
  const maxB = parsePositiveIntStrict(d.maxSelection)
  const minSel = minB ?? 0
  const maxSel = maxB ?? Number.POSITIVE_INFINITY
  const mmErr = mcCbPreviewMinMaxError(d, filledCount)

  const isMc = q.config.type === 'MULTIPLE_CHOICE'
  const useRadio = isMc && mcPreviewIsSingleSelect(d.minSelection, d.maxSelection)
  const rowsBase = d.randomize ? shuffleDeterministic(filled, `${q.id}|${filled.map((r) => r.label).join('|')}`) : filled
  const useTwoColGrid = d.layoutHorizontal && d.multiColumn
  const extra = mcCbPreviewExtraOptions(d)

  const toggleKey = (key: PreviewSelKey) => {
    setSelectedKeys((prev) =>
      computeMcCbToggleNext(prev, key, useRadio, minSel, maxSel, exclusiveKeys),
    )
  }

  const rowBtn = (key: PreviewSelKey, label: string, testId: string) => (
    <McCbPreviewOptionRow
      key={testId}
      rowKey={key}
      label={label}
      testId={testId}
      selected={selectedKeys.has(key)}
      useRadio={useRadio}
      useTwoColGrid={useTwoColGrid}
      layoutHorizontal={d.layoutHorizontal}
      mmErr={mmErr}
      maxSel={maxSel}
      exclusiveKeys={exclusiveKeys}
      selectedKeys={selectedKeys}
      onToggle={toggleKey}
    />
  )

  const selectionHint = mcCbSelectionHintText(minSel, maxSel, maxB)
  const previewBody = mcCbPreviewBodyLayout(rowsBase, extra, rowBtn, useTwoColGrid, d.layoutHorizontal)
  const showSelectionHint = !mmErr && (minSel > 0 || Number.isFinite(maxSel))
  const showSelectAllCaption = q.config.type === 'CHECKBOX' && q.config.data.selectAllLabel
  const textKey = mcCbPreviewTextKey(d)

  return (
    <Box sx={{ mt: 1 }}>
      {showSelectAllCaption ? (
        <Typography sx={{ fontSize: '12px', color: SECONDARY, mb: 1 }}>{CHECKBOX_SELECT_ALL_CAPTION}</Typography>
      ) : null}
      {showSelectionHint ? (
        <Typography sx={{ fontSize: '12px', color: SECONDARY, mb: 1 }}>{selectionHint}</Typography>
      ) : null}
      {previewBody}
      {textKey != null && selectedKeys.has(textKey) ? (
        <PreviewOtherSpecifyField
          key={textKey}
          testId={textKey === 'other' ? 'preview-other-specify' : 'preview-text-response-specify'}
          maxCharacters={parsePositiveIntStrict(d.textResponse?.maxCharacters ?? '')}
        />
      ) : null}
    </Box>
  )
}

/** BFP-1156 — preview key of the option that shows a specify field; legacy questions keep Other. */
function mcCbPreviewTextKey(d: MultipleChoiceConfig | CheckboxConfig): PreviewSelKey | null {
  const tr = d.textResponse
  if (tr === undefined) return d.other ? 'other' : null
  if (tr === null) return null
  if (tr.target === 'other') return 'other'
  if (tr.target === 'none') return 'none'
  const index = (d.optionIds ?? []).indexOf(tr.optionId ?? '')
  return index >= 0 ? optKey(index) : null
}

function computeMcCbToggleNext(
  prev: ReadonlySet<PreviewSelKey>,
  key: PreviewSelKey,
  useRadio: boolean,
  minSel: number,
  maxSel: number,
  exclusiveKeys: ReadonlySet<PreviewSelKey> = new Set(),
): ReadonlySet<PreviewSelKey> {
  const next = new Set(prev)
  const isOn = next.has(key)
  const isExclusive = !useRadio && exclusiveKeys.has(key)
  if (isOn) {
    if (useRadio) return prev
    if (!isExclusive && next.size - 1 < minSel) return prev
    next.delete(key)
    return next
  }
  if (useRadio) {
    next.clear()
    next.add(key)
    return next
  }
  if (isExclusive && [...next].some((selectedKey) => exclusiveKeys.has(selectedKey))) {
    return prev
  }
  if (next.size >= maxSel) return prev
  next.add(key)
  return next
}

function YnLivePreviewBlock({
  data,
  ynChoice,
  setYnChoice,
}: Readonly<{
  data: YesNoConfig
  ynChoice: YnPreviewChoice
  setYnChoice: Dispatch<SetStateAction<YnPreviewChoice>>
}>): ReactNode {
  /** Figma 4399:9048–9053 — Yes/No buttons: equal width row, 80×44 min, 12px gap, 14px radius, #e4e9f0 border */
  const ynPreviewButtonTypographySx = {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '21px',
    letterSpacing: '-0.1504px',
    color: '#0d1b2a',
    textAlign: 'center' as const,
  }

  if (data.toggleStyle) {
    const toggleOn = ynChoice === 'yes'
    const activeLabel = toggleOn ? data.yesLabel : data.noLabel
    return (
      <Box sx={{ mt: 1 }} data-testid="yn-live-preview-toggle">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            data-testid="yn-live-preview-toggle-label"
            sx={{ fontSize: '14px', color: '#0f172a' }}
          >
            {activeLabel}
          </Typography>
          <Switch
            checked={toggleOn}
            onChange={(_, v) => setYnChoice(v ? 'yes' : 'no')}
            sx={DISPLAY_SWITCH_SX}
            inputProps={{ 'aria-label': 'Preview yes / no toggle' }}
          />
        </Stack>
      </Box>
    )
  }
  const horizontal = data.layoutHorizontal
  /** Figma: two 80px-wide pills + 12px gap; row hugs content (`fit-content`), not full preview width */
  const ynPreviewBtnPx = 80
  const ynPreviewBtnInlineStyle = {
    width: ynPreviewBtnPx,
    height: 44,
    flexShrink: 0,
  } as const
  return (
    <Stack
      data-testid="yn-live-preview-buttons"
      direction={horizontal ? 'row' : 'column'}
      spacing={1.5}
      useFlexGap
      sx={{
        mt: 1,
        flexWrap: horizontal ? 'wrap' : 'nowrap',
        alignItems: 'stretch',
        alignSelf: 'flex-start',
        width: 'fit-content',
        maxWidth: '100%',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setYnChoice('yes')}
        style={ynPreviewBtnInlineStyle}
        sx={{
          px: 0.5,
          py: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '14px',
          bgcolor: '#fff',
          border: ynChoice === 'yes' ? `2px solid ${PRIMARY}` : '1px solid #e4e9f0',
          cursor: 'pointer',
          font: 'inherit',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          component="span"
          noWrap
          sx={{
            ...ynPreviewButtonTypographySx,
            display: 'block',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            px: 0.5,
          }}
        >
          {data.yesLabel}
        </Typography>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={() => setYnChoice('no')}
        style={ynPreviewBtnInlineStyle}
        sx={{
          px: 0.5,
          py: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '14px',
          bgcolor: '#fff',
          border: ynChoice === 'no' ? `2px solid ${PRIMARY}` : '1px solid #e4e9f0',
          cursor: 'pointer',
          font: 'inherit',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          component="span"
          noWrap
          sx={{
            ...ynPreviewButtonTypographySx,
            display: 'block',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            px: 0.5,
          }}
        >
          {data.noLabel}
        </Typography>
      </Box>
    </Stack>
  )
}

function PictureChoiceLivePreviewBlock({
  data,
  selectedKeys,
  setSelectedKeys,
}: Readonly<{
  data: PictureChoiceConfig
  selectedKeys: ReadonlySet<PreviewSelKey>
  setSelectedKeys: Dispatch<SetStateAction<ReadonlySet<PreviewSelKey>>>
}>): ReactNode {
  const filledRows = data.options.filter((o) => o.imageUrl.trim().length > 0)
  const optsErr = errorPictureOptionsNeedTwoImages(data.options)
  const dimensions = pictureChoiceDimensions(data.imageSize)
  const togglePictureKey = (key: PreviewSelKey) => {
    setSelectedKeys((prev) => {
      if (prev.has(key)) return prev
      return new Set<PreviewSelKey>([key])
    })
  }
  return (
    <Box sx={{ mt: 1 }}>
      {filledRows.length === 0 ? (
        <Typography sx={{ fontSize: '14px', color: SECONDARY }}>Add images to see them in the preview.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '19px' }}>
          {filledRows.map((row) => {
            const origIdx = data.options.findIndex((o) => o.id === row.id)
            const key = optKey(origIdx)
            const selected = selectedKeys.has(key)
            const disabled = Boolean(optsErr)
            return (
              <Box
                key={row.id}
                component="button"
                type="button"
                data-testid={`preview-picture-opt-${origIdx}`}
                data-selected={selected ? 'true' : 'false'}
                disabled={disabled}
                onClick={() => togglePictureKey(key)}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: '8px',
                  width: dimensions.width,
                  overflow: 'hidden',
                  border: selected ? `3px solid ${PRIMARY}` : '3px solid #e2e8f0',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled && !selected ? 0.5 : 1,
                  textAlign: 'left',
                  font: 'inherit',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  component="img"
                  src={row.imageUrl}
                  alt=""
                  sx={{
                    display: 'block',
                    width: dimensions.width,
                    height: dimensions.height,
                    objectFit: 'cover',
                    borderRadius: 0,
                    bgcolor: '#f0f8fd',
                    flexShrink: 0,
                  }}
                />
                {row.caption.trim() ? (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#64748b',
                      textAlign: 'center',
                      width: '100%',
                      px: '8px',
                      pb: '8px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.caption.trim()}
                  </Typography>
                ) : null}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

function DateLivePreviewBlock({
  data,
  previewDateFieldId,
  previewDate,
  setPreviewDate,
}: Readonly<{
  data: DateConfig
  previewDateFieldId: string
  previewDate: Dayjs | null
  setPreviewDate: Dispatch<SetStateAction<Dayjs | null>>
}>): ReactNode {
  const ph = data.hintPlaceholder.trim()
  const { minDate, maxDate, blockReason } = previewDateBoundsFromConfig(data)
  const pickerFmt = datePickerFormatToken(data.displayFormat)
  const disabled = Boolean(blockReason)
  /** Hint is a caption label (Figma / panel "Placeholder Text"); do not pass as TextField `placeholder` — MUI X can surface that string like a field label. */
  const previewDateTextField = {
    id: previewDateFieldId,
    fullWidth: true,
    hiddenLabel: true,
    ...(ph ? {} : { placeholder: 'Select date...' as const }),
    inputProps: { 'data-testid': 'preview-date' as const },
    sx: [
      INSTRUMENT_DATE_PICKER_FIELD_SX,
      {
        maxWidth: DATE_LIVE_PREVIEW_FIELD_MAX_WIDTH_PX,
        '& .MuiOutlinedInput-input::placeholder': {
          color: '#a0abbf',
          opacity: 1,
          fontStyle: 'italic',
        },
      },
    ],
  } as const
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mt: 1 }}>
        {ph ? (
          <Typography component="label" htmlFor={previewDateFieldId} sx={{ ...ST_FIELD_LABEL_SX, mb: 0.5 }}>
            {ph}
          </Typography>
        ) : null}
        <DatePicker
          format={pickerFmt}
          value={previewDate}
          onChange={(v) => setPreviewDate(v)}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          desktopModeMediaQuery="@media (min-width: 0px)"
          slots={INSTRUMENT_DATE_PICKER_SLOTS}
          slotProps={{
            ...INSTRUMENT_DATE_CALENDAR_SLOT_PROPS,
            textField: previewDateTextField,
          }}
        />
        {blockReason ? (
          <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
            {blockReason}
          </FormHelperText>
        ) : null}
      </Box>
    </LocalizationProvider>
  )
}

function DropdownLivePreviewBlock({
  questionId,
  data,
  ddValue,
  setDdValue,
}: Readonly<{
  questionId: string
  data: DropdownConfig
  ddValue: string
  setDdValue: Dispatch<SetStateAction<string>>
}>): ReactNode {
  const filled = filledOptionRows(data.options)
  const rows = data.randomize ? shuffleDeterministic(filled, `${questionId}|dd|${filled.map((r) => r.label).join('|')}`) : filled
  const optsErr = errorOptionsNeedTwoNonEmpty(data.options)

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        select
        hiddenLabel
        SelectProps={{
          displayEmpty: true,
          inputProps: { 'aria-label': 'Preview answer' },
          MenuProps: { disableScrollLock: true },
        }}
        size="small"
        sx={{ bgcolor: '#fff', minWidth: 220, borderRadius: '10px' }}
        value={ddValue}
        onChange={(e) => setDdValue(e.target.value)}
        data-testid="preview-dropdown"
        disabled={Boolean(optsErr)}
      >
        <MenuItem value="">Choose an option</MenuItem>
        {rows.map((r) => (
          <MenuItem key={r.index} value={String(r.index)}>
            {r.label}
          </MenuItem>
        ))}
        {data.other ? <MenuItem value="other">{PREVIEW_OTHER_LABEL}</MenuItem> : null}
        {data.noneOfAbove ? <MenuItem value="none">{PREVIEW_NONE_LABEL}</MenuItem> : null}
      </TextField>
      {ddValue === 'other' ? <PreviewOtherSpecifyField testId="preview-dropdown-other" /> : null}
    </Box>
  )
}

function PreviewOtherSpecifyField({
  testId,
  maxCharacters,
}: Readonly<{ testId: string; maxCharacters?: number }>): ReactNode {
  const [value, setValue] = useState('')
  return (
    <TextField
      size="small"
      fullWidth
      placeholder="Please specify"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      helperText={maxCharacters ? `${value.length} / ${maxCharacters}` : undefined}
      FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
      inputProps={{
        'data-testid': testId,
        'aria-label': 'Specify other',
        ...(maxCharacters ? { maxLength: maxCharacters } : {}),
      }}
      sx={{
        mt: 1.5,
        maxWidth: 480,
        bgcolor: '#fff',
        '& .MuiOutlinedInput-root': { borderRadius: '10px' },
      }}
    />
  )
}

function TextAnswerLivePreviewBlock({
  isLong,
  data,
}: Readonly<{
  isLong: boolean
  data: ShortTextConfig
}>): ReactNode {
  const maxN = parsePositiveIntStrict(data.maxLength)
  const minN = parsePositiveIntStrict(data.minLength)
  const hint =
    data.hintPlaceholder.trim() ||
    (isLong ? ST_DEFAULT_HINT_PLACEHOLDER : ST_SHORT_TEXT_LIVE_PREVIEW_DEFAULT_HINT)
  const [value, setValue] = useState('')
  return (
    <Box
      sx={{
        mt: 1,
        bgcolor: '#f0f8fd',
        border: '1px solid #e6f4fb',
        borderRadius: '10px',
        px: '17px',
        py: '21px',
        display: 'flex',
        justifyContent: isLong ? 'flex-start' : 'center',
        alignItems: isLong ? 'flex-start' : 'stretch',
      }}
    >
      <TextField
        fullWidth
        size="small"
        multiline={isLong}
        minRows={isLong ? 4 : undefined}
        placeholder={hint}
        value={value}
        onChange={(e) => {
          const next = e.target.value
          if (maxN !== undefined && next.length > maxN) return
          setValue(next)
        }}
        inputProps={{
          'data-testid': isLong ? 'preview-long-text' : 'preview-short-text',
          'aria-label': isLong ? 'Preview long answer' : 'Preview short answer',
          ...(minN === undefined ? {} : { minLength: minN }),
          ...(maxN === undefined ? {} : { maxLength: maxN }),
        }}
        sx={{
          maxWidth: 790,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            minHeight: isLong ? undefined : 44,
            bgcolor: '#fff',
            ...(isLong ? { alignItems: 'flex-start' as const } : {}),
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e9f0' },
          },
          '& .MuiOutlinedInput-input': {
            py: '11px',
            px: '14px',
            fontSize: '14px',
            ...(isLong ? { textAlign: 'left' as const } : {}),
          },
          '& .MuiOutlinedInput-input::placeholder': isLong
            ? LONG_TEXT_LIVE_PREVIEW_PLACEHOLDER_SX
            : { ...ST_OUTLINED_INPUT_PLACEHOLDER_SX, fontStyle: 'italic' as const },
        }}
      />
    </Box>
  )
}

export function QuestionLivePreview({
  q,
  showHeading = true,
  surface = 'builder',
  onPatch,
}: Readonly<{
  q: BuilderQuestion
  showHeading?: boolean
  /** Builder panel card vs instrument preview dialog (Figma 4398:6934). */
  surface?: 'builder' | 'survey'
  onPatch?: (patch: QuestionBuilderSurfacePatch) => void
}>) {
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<PreviewSelKey>>(() => new Set())
  const [ynChoice, setYnChoice] = useState<YnPreviewChoice>(null)
  const [ddValue, setDdValue] = useState('')
  const [previewDate, setPreviewDate] = useState<Dayjs | null>(null)
  const previewDateFieldId = useId()

  const resetSig = useMemo(() => {
    const c = q.config
    if (c.type === 'MULTIPLE_CHOICE') {
      const d = c.data
      return `MC|${q.id}|${d.options.join('\u0001')}|${d.minSelection}|${d.maxSelection}|${d.other}|${d.noneOfAbove}|${d.randomize}|${d.layoutHorizontal}`
    }
    if (c.type === 'PICTURE_CHOICE') {
      const d = c.data
      return ['PC', q.id, d.imageSize, fingerprintPictureOptionsForReset(d.options)].join('|')
    }
    if (c.type === 'RANKING') {
      const d = c.data
      return [
        'RK',
        q.id,
        q.questionText,
        q.description,
        d.imageSize,
        fingerprintPictureOptionsForReset(d.options),
      ].join('|')
    }
    if (c.type === 'CHECKBOX') {
      const d = c.data
      return `CB|${q.id}|${d.options.join('\u0001')}|${d.minSelection}|${d.maxSelection}|${d.other}|${d.noneOfAbove}|${d.randomize}|${d.layoutHorizontal}|${d.multiColumn}`
    }
    if (c.type === 'DROPDOWN') {
      const d = c.data
      return `DD|${q.id}|${d.options.join('\u0001')}|${d.other}|${d.noneOfAbove}|${d.randomize}`
    }
    if (c.type === 'YES_NO') {
      const d = c.data
      return `YN|${q.id}|${d.yesLabel}|${d.noLabel}|${d.toggleStyle}|${d.layoutHorizontal}`
    }
    if (c.type === 'VIDEO') {
      const d = c.data
      return `VD|${q.id}|${q.questionText}|${d.videoUrl}|${d.sourceKind}|${d.minLength}|${d.maxLength}|${d.hintPlaceholder}`
    }
    if (isTextAnswerConfig(c)) {
      const d = c.data
      const tag = c.type === 'LONG_TEXT' ? 'LT' : 'ST'
      return `${tag}|${q.id}|${q.questionText}|${q.title}|${d.minLength}|${d.maxLength}|${d.hintPlaceholder}`
    }
    if (c.type === 'DATE') {
      const d = c.data
      return `DA|${q.id}|${q.questionText}|${q.description}|${d.earliestSelectableDate}|${d.latestSelectableDate}|${d.disablePastDates}|${d.disableFutureDates}|${d.hintPlaceholder}|${d.displayFormat}`
    }
    if (c.type === 'RATING') {
      const d = c.data
      return `RT|${q.id}|${q.questionText}|${q.description}|${d.scaleMax}|${d.displayStyle}|${d.lowLabel}|${d.highLabel}`
    }
    if (c.type === 'LIKERT') {
      const d = c.data
      return `LK|${q.id}|${q.questionText}|${q.description}|${d.pointCount}|${d.layoutHorizontal}|${d.labels.join('\u0001')}`
    }
    if (c.type === 'SLIDER') {
      const d = c.data
      return `SL|${q.id}|${q.questionText}|${q.description}|${d.minValue}|${d.maxValue}|${d.step}|${d.lowLabel}|${d.highLabel}`
    }
    return `${q.type}|${q.id}`
  }, [q])

  useEffect(() => {
    setSelectedKeys(new Set())
    setYnChoice(null)
    setDdValue('')
    setPreviewDate(null)
  }, [resetSig])

  const heading = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {showHeading ? <VisibilityOutlinedIcon sx={{ fontSize: 18, color: PRIMARY }} aria-hidden /> : null}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '18px',
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: '#008cd0',
        }}
      >
        LIVE PREVIEW
      </Typography>
    </Stack>
  )

  const headline = instrumentPreviewQuestionPrompt(q)
  const descNode = instrumentPreviewQuestionDescription(q)

  const cardSx = {
    bgcolor: PREVIEW_CARD_BG,
    border: `1px solid ${PREVIEW_CARD_BORDER}`,
    borderRadius: '10px',
    p: 2,
    minHeight: 160,
  } as const

  const core = (() => {
    if (q.config.type === 'YES_NO') {
      return <YnLivePreviewBlock data={q.config.data} ynChoice={ynChoice} setYnChoice={setYnChoice} />
    }

    if (q.config.type === 'PICTURE_CHOICE') {
      return (
        <PictureChoiceLivePreviewBlock data={q.config.data} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} />
      )
    }

    if (q.config.type === 'RANKING') {
      const d = q.config.data
      const optsErr = errorRankingOptionsNeedTwoNonEmpty(d.options)
      return <RankingLivePreviewBody data={d} optsErr={optsErr} resetSig={resetSig} />
    }

    if (q.config.type === 'RATING') {
      return <RatingLivePreviewBlock data={q.config.data} resetSig={resetSig} />
    }

    if (q.config.type === 'LIKERT') {
      return <LikertLivePreviewBlock data={q.config.data} resetSig={resetSig} />
    }

    if (q.config.type === 'SLIDER') {
      return (
        <SliderLivePreviewBlock
          data={q.config.data}
          resetSig={resetSig}
          rangeError={sliderRangeValidationMessage(q.config.data)}
          onPatch={onPatch}
        />
      )
    }

    if (q.config.type === 'MULTIPLE_CHOICE' || q.config.type === 'CHECKBOX') {
      return <McCbLivePreviewBlock q={q} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} />
    }

    if (q.config.type === 'DATE') {
      return (
        <DateLivePreviewBlock
          data={q.config.data}
          previewDateFieldId={previewDateFieldId}
          previewDate={previewDate}
          setPreviewDate={setPreviewDate}
        />
      )
    }

    if (q.config.type === 'DROPDOWN') {
      return (
        <DropdownLivePreviewBlock
          questionId={q.id}
          data={q.config.data}
          ddValue={ddValue}
          setDdValue={setDdValue}
        />
      )
    }

    if (q.config.type === 'VIDEO') {
      return (
        <Box>
          <Box sx={{ mb: 2 }}>
            <VideoPlaybackEmbed videoUrl={q.config.data.videoUrl} testId="preview-video" />
          </Box>
          <TextAnswerLivePreviewBlock key={resetSig} isLong={false} data={q.config.data} />
        </Box>
      )
    }

    if (isTextAnswerConfig(q.config)) {
      return <TextAnswerLivePreviewBlock key={resetSig} isLong={q.config.type === 'LONG_TEXT'} data={q.config.data} />
    }

    return (
      <Typography sx={{ mt: 1, fontSize: '14px', color: SECONDARY }}>
        Live preview for this question type is not available in the builder yet.
      </Typography>
    )
  })()

  if (surface === 'survey') {
    return <Box data-testid="question-live-preview-survey">{core}</Box>
  }

  return (
    <Box sx={{ mt: showHeading ? 3 : 0 }}>
      {showHeading ? heading : null}
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.35, color: '#0f172a', mb: descNode ? 0.5 : 1.5 }}>
          {headline}
          {q.mandatory ? <InstrumentPreviewRequiredMark /> : null}
        </Typography>
        {descNode ? (
          <Typography sx={{ fontSize: '14px', color: SECONDARY, whiteSpace: 'pre-wrap', mb: 1.5 }}>
            {descNode}
          </Typography>
        ) : null}
        {core}
      </Box>
    </Box>
  )
}
